import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import ProductAttributesFields, {
  attributesFromProduct, attributesToPayload,
} from "../components/retail/admin/ProductAttributesFields";
import DashboardLayout from "../components/DashboardLayout";
import ConfirmModal from "../components/ConfirmModal";
import ExtractFromUrlPanelV2 from "../components/retail/ExtractFromUrlPanelV2";
import MenuDiscoveryPanel from "../components/retail/MenuDiscoveryPanel";
import AdminDataGrid, { ToggleCell } from "../components/admin/AdminDataGrid";
import ImportHistorySection from "../components/retail/ImportHistorySection";
import CatalogFulfillmentPanel from "../components/retail/admin/CatalogFulfillmentPanel";
import CatalogSchedulingPanel from "../components/retail/admin/CatalogSchedulingPanel";
import CategoryOrderPanel from "../components/retail/admin/CategoryOrderPanel";
import LoyaltyOtpVerificationSetting from "../components/retail/admin/LoyaltyOtpVerificationSetting";
import ProductImageUploader from "../components/retail/admin/ProductImageUploader";
import ProductTaxTreatmentFields, {
  taxTreatmentFromProduct,
  taxTreatmentPayload,
  taxTreatmentChanged,
  taxTreatmentLabel,
} from "../components/retail/admin/ProductTaxTreatmentFields";
import { toast } from "sonner";
import { Loader2, Plus, Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, ExternalLink, Copy, Trash2, RefreshCw, ShoppingBag, Search, Edit3, Check, X, RotateCcw, Files } from "lucide-react";

// Phase 3G — Retail Catalog setup for tenants. Single mounted page at
// /dashboard/retail-catalogs. Drives the entire onboarding flow:
//   - Create a catalog
//   - Upload CSV/XLSX -> detect -> preview -> commit
//   - Publish/draft/archive
//   - Browse imported products
//   - Link to a retail assistant
//   - Surface the public /retail/{slug} link with Copy + Open

// ----------------------- helpers -----------------------------------------

const STATUS_STYLES = {
  draft: "bg-amber-100 text-amber-800 border-amber-200",
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  archived: "bg-slate-100 text-slate-600 border-slate-200",
};

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

function fmtMoney(amount, currency) {
  if (amount == null) return "";
  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "").toUpperCase()] || "";
  return sym ? `${sym}${Number(amount).toFixed(2)}` : `${Number(amount).toFixed(2)} ${currency || ""}`.trim();
}

// ----------------------- top-level page ----------------------------------

export default function RetailCatalogsPage() {
  const [company, setCompany] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retailGate, setRetailGate] = useState({ ready: false, enabled: false });
  const [activeCatalogId, setActiveCatalogId] = useState(null);
  // Iter42 — Archive visibility toggle. When ON, the catalogs list also
  // includes archived items (each rendered with a restore inline action).
  const [showArchived, setShowArchived] = useState(false);

  const loadCompany = () =>
    api.get("/company").then((r) => {
      setCompany(r.data?.company || null);
      const enabled = Boolean(r.data?.company?.retail_mode_enabled);
      setRetailGate({ ready: true, enabled });
    });

  const loadCatalogs = (opts = {}) => {
    const includeArchived = Object.prototype.hasOwnProperty.call(opts, "includeArchived")
      ? Boolean(opts.includeArchived)
      : showArchived;
    const params = includeArchived ? { include_archived: true } : {};
    return api.get("/retail/catalogs", { params })
      .then((r) => {
        setCatalogs(r.data.catalogs || []);
        if (!activeCatalogId && (r.data.catalogs || []).length > 0) {
          setActiveCatalogId(r.data.catalogs[0].id);
        }
      })
      .catch((e) => {
        if (e?.response?.status === 403) {
          setRetailGate({ ready: true, enabled: false });
        }
      });
  };

  // Re-fetch the list whenever the archive toggle flips. We pass the value
  // explicitly because React state setters are async — relying on the
  // closure value of `showArchived` would lag one render behind.
  useEffect(() => {
    if (!retailGate.ready || !retailGate.enabled) return;
    loadCatalogs({ includeArchived: showArchived });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadCompany(), loadCatalogs()])
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-16 grid place-items-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (retailGate.ready && !retailGate.enabled) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl space-y-3" data-testid="retail-disabled-banner">
          <h1 className="text-2xl font-semibold tracking-tight">Retail Catalog</h1>
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6">
            <div className="flex items-start gap-3">
              <ShoppingBag className="w-5 h-5 text-slate-500 mt-0.5" />
              <div>
                <div className="font-medium text-slate-900">Retail mode isn&apos;t enabled for this account.</div>
                <p className="text-sm text-slate-600 mt-1">
                  Ask your Rudras admin to switch on the Retail engine for this tenant.
                  They can do this from <strong>Internal Admin → Tenants → Retail toggle</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const slug = company?.chatbot?.slug || company?.chatbot_slug;
  const activeCatalog = catalogs.find((c) => c.id === activeCatalogId);
  const hasPublished = catalogs.some((c) => c.status === "published");

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="retail-catalogs-page">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Retail Catalog</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your retail menus. Upload a CSV or XLSX, map columns, then publish to open your storefront.
            </p>
          </div>
          {slug && hasPublished && <PublicLinkPill slug={slug} />}
        </header>

        <CatalogList
          catalogs={catalogs}
          activeId={activeCatalogId}
          onSelect={setActiveCatalogId}
          onChanged={loadCatalogs}
          showArchived={showArchived}
          onToggleArchived={setShowArchived}
        />

        {activeCatalog && (
          <CatalogDetail
            catalog={activeCatalog}
            onChanged={loadCatalogs}
            onSelect={setActiveCatalogId}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

// ----------------------- public link pill --------------------------------

function PublicLinkPill({ slug }) {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}/retail/${slug}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard might be blocked — ignore */ }
  };

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-2 text-sm" data-testid="retail-public-link-pill">
      <div>
        <div className="text-[10px] uppercase tracking-wide text-emerald-700">Public retail link</div>
        <div className="font-mono text-xs text-slate-700 break-all" data-testid="retail-public-link-url">{url}</div>
        <div className="text-[11px] text-slate-500 mt-1" data-testid="retail-public-link-helper">
          Share this with customers — it opens your default published storefront where they can browse menu items and place an order.
        </div>
      </div>
      <button
        onClick={copy}
        data-testid="retail-public-link-copy"
        className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs"
      >
        <Copy className="w-3.5 h-3.5" /> {copied ? "Copied" : "Copy"}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        data-testid="retail-public-link-open"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 text-white text-xs hover:bg-emerald-700"
      >
        <ExternalLink className="w-3.5 h-3.5" /> Open
      </a>
    </div>
  );
}

// ----------------------- catalog list ------------------------------------

function CatalogList({ catalogs, activeId, onSelect, onChanged, showArchived, onToggleArchived }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [submitting, setSubmitting] = useState(false);
  // Iter42 — inline busy lock for the per-card Restore action so two
  // rapid clicks can't fire concurrent PATCH requests against the same
  // catalog id.
  const [restoringId, setRestoringId] = useState(null);

  const create = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/retail/catalogs", { name, description, currency });
      onSelect(r.data.catalog.id);
      setName(""); setDescription(""); setCreating(false);
      await onChanged();
      toast.success(`Catalog “${r.data.catalog.name}” created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create catalog");
    } finally { setSubmitting(false); }
  };

  const restoreCatalog = async (e, c) => {
    // Card is a <button> — stop propagation so we don't also fire onSelect.
    e.stopPropagation();
    e.preventDefault();
    if (restoringId) return;
    setRestoringId(c.id);
    try {
      await api.post(`/retail/catalogs/${c.id}/restore`);
      toast.success(`Restored “${c.name}” to draft`);
      onSelect(c.id);
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore catalog");
    } finally { setRestoringId(null); }
  };

  return (
    <section className="space-y-3" data-testid="retail-catalog-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold">Catalogs</h2>
        <div className="flex items-center gap-2">
          {/* Iter42 — Show archived toggle. OFF by default, persisted in
              parent state so the list reloads with include_archived=true. */}
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="retail-show-archived-label">
            <input
              type="checkbox"
              checked={Boolean(showArchived)}
              onChange={(e) => onToggleArchived(e.target.checked)}
              data-testid="retail-show-archived-toggle"
              className="h-3.5 w-3.5 accent-slate-700 cursor-pointer"
            />
            Show archived
          </label>
          <button
            data-testid="retail-create-catalog-btn"
            onClick={() => setCreating((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" /> {creating ? "Cancel" : "New catalog"}
          </button>
        </div>
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="retail-create-catalog-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-slate-500 mb-1">Catalog name *</div>
              <input
                data-testid="retail-create-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"
                placeholder="Lunch menu"
              />
            </label>
            <label className="block">
              <div className="text-xs text-slate-500 mb-1">Currency</div>
              <select
                data-testid="retail-create-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white"
              >
                <option>USD</option><option>INR</option><option>EUR</option><option>GBP</option>
              </select>
            </label>
          </div>
          <label className="block">
            <div className="text-xs text-slate-500 mb-1">Description (optional)</div>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            data-testid="retail-create-submit"
            className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create catalog"}
          </button>
        </form>
      )}

      {catalogs.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/40 px-6 py-10 text-center space-y-3"
          data-testid="retail-catalog-empty"
        >
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div className="text-base font-medium text-slate-900">Your first menu is one upload away</div>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            Create a draft catalog, import your menu from a spreadsheet, PDF, or website, review the preview, and publish when you&apos;re happy.
            Nothing goes live until you press Publish.
          </p>
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="retail-catalog-empty-cta"
            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus className="w-4 h-4" /> Create my first catalog
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {catalogs.map((c) => {
            const isArchived = (c.status || "").toLowerCase() === "archived";
            return (
              <button
                key={c.id}
                data-testid={`retail-catalog-card-${c.id}`}
                onClick={() => onSelect(c.id)}
                className={
                  "text-left bg-white border rounded-xl p-4 transition " +
                  (activeId === c.id ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-300") +
                  (isArchived ? " opacity-70" : "")
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium truncate">{c.name}</div>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLES[c.status] || ""}`}>
                    {c.status}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-2">{c.product_count} products · {c.currency}</div>
                <div className="text-[10px] text-slate-400 mt-1">Updated {fmt(c.updated_at)}</div>
                {isArchived && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-slate-500">Hidden from storefront</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => restoreCatalog(e, c)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") restoreCatalog(e, c); }}
                      data-testid={`retail-catalog-restore-${c.id}`}
                      aria-disabled={restoringId === c.id}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-slate-900 text-white hover:bg-slate-800 cursor-pointer select-none"
                    >
                      {restoringId === c.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <RotateCcw className="w-3 h-3"/>}
                      Restore
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ----------------------- catalog detail ----------------------------------

function CatalogDetail({ catalog, onChanged, onSelect }) {
  const [products, setProducts] = useState({ items: [], total: 0 });
  const [busy, setBusy] = useState(false);
  const [quality, setQuality] = useState(null);  // Phase 3I.5A — shared with publish guard
  const [editing, setEditing] = useState(false); // Phase 3L.1 — rename modal
  const [duplicating, setDuplicating] = useState(false); // Iter42 — duplicate modal
  const [addingProduct, setAddingProduct] = useState(false); // Phase 3L.1 — manual product modal
  // Phase 3L.1 Pass B — confirm dialogs (replaces window.confirm).
  const [publishDespiteLowQuality, setPublishDespiteLowQuality] = useState(null); // { grade, score, tips }
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const loadProducts = () =>
    api.get(`/retail/catalogs/${catalog.id}/products`)
      .then((r) => setProducts({ items: r.data.items || [], total: r.data.total || 0 }))
      .catch(() => setProducts({ items: [], total: 0 }));

  const loadQuality = () =>
    api.get(`/retail/catalogs/${catalog.id}/quality`)
      .then((r) => setQuality(r.data?.quality || null))
      .catch(() => setQuality(null));

  useEffect(() => {
    loadProducts();
    loadQuality();
    /* eslint-disable-next-line */
  }, [catalog.id, catalog.updated_at]);

  // Phase 3I.5A — Publish Confidence Guard. Soft warning when grade ≤ D.
  // Never blocks publishing — the operator decides.
  const setStatus = async (status) => {
    if (status === "published" && quality && !quality.vertical_unsupported) {
      const lowGrade = quality.grade === "D" || quality.grade === "F";
      if (lowGrade) {
        // Phase 3L.1 Pass B — defer to in-app modal; finishPublish() picks
        // up the flow once the operator confirms.
        setPublishDespiteLowQuality({ grade: quality.grade, score: quality.score, tips: (quality.tips || []).slice(0, 2) });
        return;
      }
    }
    await applyStatus(status);
  };

  const applyStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/retail/catalogs/${catalog.id}/status`, { status });
      await onChanged();
      await loadQuality();
      const labels = { published: "Catalog published — your storefront is live", draft: "Moved to draft", archived: "Catalog archived" };
      toast.success(labels[status] || "Status updated");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Parcel Shipping Readiness Completion — the backend rejects
      // publish with a structured { code: "parcel_shipping_not_ready",
      // missing: [{code, message}, ...] } body instead of a plain
      // string, so the operator sees exactly what's missing right here
      // instead of a generic "Status update failed".
      if (detail && typeof detail === "object" && detail.code === "parcel_shipping_not_ready") {
        const items = (detail.missing || []).map((m) => `• ${m.message}`).join("\n");
        toast.error("Can't publish — Parcel Shipping isn't ready yet", {
          description: items || "See the Parcel Shipping → Readiness tab for details.",
          duration: 12000,
        });
      } else {
        toast.error((typeof detail === "string" && detail) || "Status update failed");
      }
    } finally { setBusy(false); }
  };

  const removeCatalog = () => setDeleteConfirm(true);

  const doRemoveCatalog = async () => {
    setDeleteConfirm(false);
    setBusy(true);
    try {
      await api.delete(`/retail/catalogs/${catalog.id}`);
      await onChanged();
      toast.success("Catalog deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="space-y-4 bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-5" data-testid={`retail-catalog-detail-${catalog.id}`}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Catalog</div>
          <div className="text-lg font-semibold flex items-center gap-2 flex-wrap">
            {catalog.name}
            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLES[catalog.status] || ""}`} data-testid="retail-detail-status">
              {catalog.status}
            </span>
            {catalog.display_code && (
              <CopyChip value={catalog.display_code} testid={`retail-catalog-code-${catalog.id}`}/>
            )}
          </div>
          {catalog.description && <div className="text-xs text-slate-500 mt-1">{catalog.description}</div>}
          {/* Draft vs Published clarity for first-time operators. */}
          <div className="text-[11px] text-slate-500 mt-1" data-testid="retail-catalog-status-help">
            {catalog.status === "draft" && "Draft — only you can see this. Customers won’t see edits until you publish."}
            {catalog.status === "published" && "Published — live on your public storefront. Edits apply instantly."}
            {catalog.status === "archived" && "Archived — hidden from storefront and assistant. Move back to draft to edit."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {catalog.status !== "published" && quality && !quality.vertical_unsupported && (quality.grade === "D" || quality.grade === "F") && (
            <span
              className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200"
              data-testid="retail-publish-low-quality-hint"
              title={`Menu quality ${quality.grade} (${quality.score}/100) — customers may see incomplete answers.`}
            >
              Menu {quality.grade} · review before publish
            </span>
          )}
          {catalog.status !== "published" && catalog.status !== "archived" && (
            <button
              data-testid="retail-publish-btn"
              disabled={busy}
              onClick={() => setStatus("published")}
              className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
            >
              Publish
            </button>
          )}
          {catalog.status === "published" && (
            <button
              data-testid="retail-unpublish-btn"
              disabled={busy}
              onClick={() => setStatus("draft")}
              className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm hover:bg-slate-100 disabled:opacity-60"
            >
              Move to draft
            </button>
          )}
          {catalog.status === "archived" ? (
            // Iter42 — archived catalogs surface a single Restore action
            // (restores to draft). Publish/Archive/Duplicate are hidden
            // until the operator restores so the bar stays unambiguous.
            <button
              data-testid="retail-restore-btn"
              disabled={busy}
              onClick={() => setStatus("draft")}
              title="Restore this catalog back to draft so you can edit and republish it."
              className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restore to draft
            </button>
          ) : (
            <>
              <button
                data-testid="retail-archive-btn"
                disabled={busy}
                onClick={() => setStatus("archived")}
                className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm hover:bg-slate-100 disabled:opacity-60"
              >
                Archive
              </button>
              <button
                data-testid="retail-edit-catalog-btn"
                disabled={busy}
                onClick={() => setEditing(true)}
                title="Edit name, description, currency"
                className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm hover:bg-slate-100 disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
              <button
                data-testid="retail-duplicate-btn"
                disabled={busy}
                onClick={() => setDuplicating(true)}
                title="Duplicate this catalog — opens options for what to copy"
                className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm hover:bg-slate-100 disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                <Files className="w-3.5 h-3.5" /> Duplicate
              </button>
            </>
          )}
          <button
            data-testid="retail-delete-btn"
            disabled={busy}
            onClick={removeCatalog}
            title="Delete catalog"
            className="h-9 w-9 grid place-items-center rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </header>

      <OrderModesPanel catalog={catalog} onChanged={onChanged} />

      <StorefrontAndChatPanel catalog={catalog} onChanged={onChanged} />

      <CatalogFulfillmentPanel catalog={catalog} onChanged={onChanged} />

      <CatalogSchedulingPanel catalog={catalog} />

      <ImportExistingMenuSection
        catalog={catalog}
        onChanged={onChanged}
        loadProducts={loadProducts}
      />

      <MenuQualityPanel catalog={catalog} reloadKey={catalog.updated_at + ":" + products.total} />

      <ProductAttributesPanel catalog={catalog} onChanged={onChanged} />

      <CategoryOrderPanel
        catalog={catalog}
        onChanged={onChanged}
      />

      <AttributeDictionaryPanel catalog={catalog} />

      <RetailCatalogSchemaPanel catalog={catalog} />

      <ProductTable
        catalog={catalog}
        refreshKey={products.total + "::" + catalog.updated_at}
        onReload={() => { loadProducts(); loadQuality(); }}
        onAddManual={() => setAddingProduct(true)}
      />

      <AssistantLinkSection catalog={catalog} />

      {editing && (
        <CatalogEditModal
          catalog={catalog}
          onClose={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await onChanged(); }}
        />
      )}
      {duplicating && (
        <DuplicateCatalogModal
          catalog={catalog}
          onClose={() => setDuplicating(false)}
          onDuplicated={async (newCatalog) => {
            setDuplicating(false);
            await onChanged();
            // Auto-select the freshly created draft so the operator lands
            // right on it for renaming / publishing.
            if (newCatalog?.id && onSelect) onSelect(newCatalog.id);
          }}
        />
      )}
      {addingProduct && (
        <ManualProductModal
          catalog={catalog}
          onClose={() => setAddingProduct(false)}
          onSaved={async () => { setAddingProduct(false); await loadProducts(); await onChanged(); }}
        />
      )}

      <ConfirmModal
        open={Boolean(publishDespiteLowQuality)}
        title="Publish anyway with low menu quality?"
        message={publishDespiteLowQuality ? `Your menu quality is currently ${publishDespiteLowQuality.grade} (${publishDespiteLowQuality.score}/100). Customers may see incomplete answers and missing fields.` + (publishDespiteLowQuality.tips.length ? `\n\nTop gaps:\n• ${publishDespiteLowQuality.tips.join("\n• ")}` : "") : ""}
        confirmLabel="Publish anyway"
        tone="warning"
        busy={busy}
        onCancel={() => setPublishDespiteLowQuality(null)}
        onConfirm={async () => { setPublishDespiteLowQuality(null); await applyStatus("published"); }}
        testIdPrefix="retail-catalog-publish-confirm"
      />
      <ConfirmModal
        open={deleteConfirm}
        title="Delete this catalog?"
        message={`"${catalog.name}" and ALL its products will be permanently removed. This cannot be undone. Existing orders are unaffected.`}
        confirmLabel="Delete catalog"
        tone="danger"
        busy={busy}
        onCancel={() => setDeleteConfirm(false)}
        onConfirm={doRemoveCatalog}
        testIdPrefix="retail-catalog-delete-confirm"
      />
    </section>
  );
}

// ----------------------- menu quality panel ------------------------------
// Phase 3I — Restaurant-specific quality snapshot. Tiny tile rendered
// below the import wizard so operators see exactly what's missing
// before they go live. Restaurant template only; other verticals
// will get their own quality rubric in future phases.
function MenuQualityPanel({ catalog, reloadKey }) {
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/retail/catalogs/${catalog.id}/quality`)
      .then((r) => { if (alive) setQuality(r.data?.quality || null); })
      .catch(() => { if (alive) setQuality(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.id, reloadKey]);

  if (loading || !quality) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="retail-quality-panel">
        <div className="text-xs uppercase tracking-wide text-slate-500">Menu accuracy</div>
        <div className="text-sm text-slate-500 mt-1">{loading ? "Checking catalog quality…" : "Quality data unavailable."}</div>
      </div>
    );
  }
  if (quality.vertical_unsupported) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="retail-quality-panel">
        <div className="text-xs uppercase tracking-wide text-slate-500">Menu accuracy</div>
        <div className="text-sm text-slate-500 mt-1">
          Quality scoring for <span className="font-mono">{quality.vertical}</span> catalogs is coming in a future phase. Each industry will have its own dedicated template.
        </div>
      </div>
    );
  }

  const gradeColors = {
    A: "bg-emerald-100 text-emerald-800 border-emerald-200",
    B: "bg-emerald-50 text-emerald-700 border-emerald-100",
    C: "bg-amber-100 text-amber-800 border-amber-200",
    D: "bg-orange-100 text-orange-800 border-orange-200",
    F: "bg-rose-100 text-rose-800 border-rose-200",
  };
  const bar = (label, pct, testId) => (
    <div className="flex items-center gap-2 text-xs">
      <div className="w-32 text-slate-600 truncate">{label}</div>
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-slate-700 transition-all"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
      <div className="w-10 text-right tabular-nums text-slate-700" data-testid={testId}>{pct}%</div>
    </div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="retail-quality-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Menu accuracy</div>
          <div className="text-sm text-slate-500">
            Sales catalog template · {quality.total_products} product{quality.total_products === 1 ? "" : "s"}
            {quality.missing_price_count > 0 && (
              <span className="ml-2 text-rose-600" data-testid="retail-quality-missing-price">
                {quality.missing_price_count} missing price
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-base font-semibold w-10 h-10 grid place-items-center rounded-full border ${gradeColors[quality.grade] || "bg-slate-100 text-slate-700 border-slate-200"}`}
            data-testid="retail-quality-grade"
            title={`Score ${quality.score}/100`}
          >
            {quality.grade}
          </span>
          <div className="text-xs text-slate-500" data-testid="retail-quality-score">
            {quality.score}/100
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        {bar("Descriptions",  quality.with_description_pct,  "retail-quality-pct-description")}
        {bar("Categories",    quality.with_category_pct,     "retail-quality-pct-category")}
        {bar("Photos",        quality.with_image_pct,        "retail-quality-pct-image")}
        {bar("Allergen data", quality.with_allergens_pct,    "retail-quality-pct-allergens")}
        {bar("Ingredients",   quality.with_ingredients_pct,  "retail-quality-pct-ingredients")}
      </div>
      {quality.tips && quality.tips.length > 0 && (
        <ul className="text-xs text-slate-600 list-disc pl-5 space-y-1" data-testid="retail-quality-tips">
          {quality.tips.slice(0, 3).map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ----------------------- import existing menu section -------------------
// Phase 3I.5 — wraps three onboarding paths under a single discoverable
// section. Spreadsheet preserves the existing CSV/XLSX wizard. PDF and
// Website use the new extraction endpoints; their output is always a
// DRAFT preview that the operator reviews + edits before commit. The
// commit step refuses non-draft catalogs (server enforces 409), so
// extracted rows can never leak onto the live storefront before review.

function ImportExistingMenuSection({ catalog, onChanged, loadProducts }) {
  const [tab, setTab] = useState("spreadsheet");
  const isDraft = (catalog.status || "").toLowerCase() === "draft";
  const handleCommitted = async () => { await loadProducts(); await onChanged(); };
  const tabs = [
    { id: "spreadsheet", label: "Spreadsheet", testid: "retail-import-tab-spreadsheet" },
    { id: "pdf",         label: "PDF menu",    testid: "retail-import-tab-pdf" },
    { id: "url",         label: "Website",     testid: "retail-import-tab-url" },
    { id: "discovery",   label: "Import Menu (One-Time)",   testid: "retail-import-tab-discovery" },
  ];
  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-4"
      data-testid="retail-import-existing-menu"
    >
      <header className="space-y-1">
        <h3 className="text-base font-semibold text-slate-900">Import existing menu</h3>
        <p className="text-xs text-slate-500">
          Convert a menu you already have into draft products. Spreadsheet
          is the most accurate path; PDF and website are best-effort
          assistance that you review before publishing.
        </p>
        {!isDraft && (tab === "pdf" || tab === "url" || tab === "discovery") && (
          <p
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1"
            data-testid="retail-import-draft-required-banner"
          >
            PDF, website, and discovery commits are only allowed on draft
            catalogs. Move this catalog back to draft, or create a new
            draft catalog for the import. (CSV/XLSX export still works.)
          </p>
        )}
      </header>
      <nav className="flex gap-1 border-b border-slate-200" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => setTab(t.id)}
            className={
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition " +
              (tab === t.id
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-900")
            }
            role="tab"
            aria-selected={tab === t.id}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {tab === "spreadsheet" && <ImportWizard catalog={catalog} onCommitted={handleCommitted} />}
      {tab === "pdf" && (
        <ExtractFromPdfPanel catalog={catalog} isDraft={isDraft} onCommitted={handleCommitted} />
      )}
      {tab === "url" && (
        <ExtractFromUrlPanelV2 catalog={catalog} isDraft={isDraft} onCommitted={handleCommitted} />
      )}
      {tab === "discovery" && (
        <MenuDiscoveryPanel catalog={catalog} isDraft={isDraft} onCommitted={handleCommitted} />
      )}
    </section>
  );
}

// Normalise the extraction-response payload into a single candidate array.
// The backend extract/url + extract/pdf endpoints return the rows under
// `preview` (alongside `summary`, `notes`, `duplicates`). A previous build of
// this file mistakenly read `payload.products`, which exists ONLY for the
// commit endpoint — producing the contradictory "N extracted / 0 items
// found" UI. We keep both keys plus a few likely aliases so any future
// contract drift is rendered, not silently dropped.
function normaliseExtractionRows(payload) {
  if (!payload) return [];
  const arr =
    (Array.isArray(payload.preview) && payload.preview) ||
    (Array.isArray(payload.products) && payload.products) ||
    (Array.isArray(payload.candidates) && payload.candidates) ||
    (Array.isArray(payload.extracted_products) && payload.extracted_products) ||
    (Array.isArray(payload.items) && payload.items) ||
    [];
  return arr;
}


// ----------------------- extraction preview helpers ----------------------

function ExtractionPreviewTable({ products, preview, onCommit, busy, isDraft, catalogId }) {
  // Tracks which rows are selected for commit + edits to name/price/category.
  const [rows, setRows] = useState(() => buildInitialRows(products));
  const [projected, setProjected] = useState(null);  // { current, projected, applied_count }
  const [projecting, setProjecting] = useState(false);

  function buildInitialRows(list) {
    const decorated = list.map((p) => ({ ...p, _selected: true }));
    // Needs-review rows float to the top so operators see them first.
    return decorated.sort((a, b) => {
      const aw = (a.extraction_warnings || []).length > 0 ? 1 : 0;
      const bw = (b.extraction_warnings || []).length > 0 ? 1 : 0;
      return bw - aw;
    });
  }

  React.useEffect(() => {
    setRows(buildInitialRows(products));
    setProjected(null);
  }, [products]);

  // Phase 3I.5A — recompute projected quality whenever selection/edits
  // change. Debounced ~500ms to avoid hammering the API on every keystroke.
  React.useEffect(() => {
    if (!catalogId) return;
    const handle = setTimeout(async () => {
      const toScore = rows
        .filter((r) => r._selected)
        .map(({ _selected, ...rest }) => rest);
      setProjecting(true);
      try {
        const r = await api.post(`/retail/catalogs/${catalogId}/quality/preview`, { products: toScore });
        setProjected(r.data);
      } catch {
        setProjected(null);
      } finally {
        setProjecting(false);
      }
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, catalogId]);

  const toggle = (idx) => {
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, _selected: !row._selected } : row)));
  };
  const editField = (idx, field, value) => {
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  };
  const selectedCount = rows.filter((r) => r._selected).length;
  const needsReviewCount = rows.filter(
    (r) => Array.isArray(r.extraction_warnings) && r.extraction_warnings.length > 0
  ).length;

  const submit = () => {
    const toCommit = rows
      .filter((r) => r._selected)
      .map(({ _selected, ...rest }) => rest);
    onCommit(toCommit);
  };

  return (
    <div className="space-y-3" data-testid="retail-extract-preview">
      {/* Phase 3I.5A — Extraction summary card (server-computed counts). */}
      {preview?.summary && (
        <ExtractionSummaryCard
          summary={preview.summary}
          duplicates={preview.duplicates}
        />
      )}
      {/* Phase 3I.5A — Projected quality banner. */}
      {projected && (
        <ProjectedQualityBanner projected={projected} loading={projecting} />
      )}
      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
        <span>{rows.length} item{rows.length === 1 ? "" : "s"} found</span>
        {needsReviewCount > 0 && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200"
            data-testid="retail-extract-needs-review-count"
          >
            {needsReviewCount} need{needsReviewCount === 1 ? "s" : ""} review
          </span>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
          {rows.length === 0 && (
            <div className="p-4 text-sm text-slate-500" data-testid="retail-extract-empty">
              Nothing extracted. Try a clearer source or use the Spreadsheet tab.
            </div>
          )}
          {rows.map((row, idx) => {
            const warnings = Array.isArray(row.extraction_warnings) ? row.extraction_warnings : [];
            const hasWarning = warnings.length > 0;
            const dupOf = row.duplicate_of;
            return (
              <React.Fragment key={idx}>
              <div
                className={"flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 " + (hasWarning ? "bg-amber-50/40" : "")}
                data-testid={`retail-extract-row-${idx}`}
              >
                <input
                  type="checkbox"
                  checked={row._selected}
                  onChange={() => toggle(idx)}
                  className="shrink-0 w-4 h-4 accent-slate-900"
                  data-testid={`retail-extract-row-select-${idx}`}
                />
                {/* Phase 3I.5C — thumbnail + clear button for extracted image URL. */}
                {row.image_url ? (
                  <div className="relative shrink-0" data-testid={`retail-extract-thumb-${idx}`}>
                    <img
                      src={row.image_url}
                      alt=""
                      loading="lazy"
                      className="w-12 h-12 object-cover rounded-md border border-slate-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => editField(idx, "image_url", "")}
                      className="absolute -top-1 -right-1 w-4 h-4 grid place-items-center rounded-full bg-white border border-slate-300 text-[10px] text-slate-600 hover:bg-rose-50 hover:text-rose-700"
                      title="Clear image"
                      data-testid={`retail-extract-thumb-clear-${idx}`}
                    >×</button>
                  </div>
                ) : (
                  <div
                    className="w-12 h-12 rounded-md border border-dashed border-slate-200 grid place-items-center text-[9px] text-slate-400 shrink-0"
                    data-testid={`retail-extract-thumb-empty-${idx}`}
                  >no img</div>
                )}
                <input
                  className="flex-1 min-w-0 text-sm border border-slate-200 rounded-md px-2 py-1"
                  value={row.name || ""}
                  onChange={(e) => editField(idx, "name", e.target.value)}
                  placeholder="Item name"
                />
                <input
                  type="number"
                  step="0.01"
                  className="w-24 text-sm border border-slate-200 rounded-md px-2 py-1"
                  value={row.price === null || row.price === undefined ? "" : row.price}
                  onChange={(e) => {
                    const v = e.target.value;
                    editField(idx, "price", v === "" ? null : Number(v));
                  }}
                  placeholder="Price"
                />
                <input
                  className="w-32 text-sm border border-slate-200 rounded-md px-2 py-1"
                  value={row.category || ""}
                  onChange={(e) => editField(idx, "category", e.target.value)}
                  placeholder="Category"
                />
                <div className="flex flex-wrap gap-1 min-w-0">
                  {hasWarning && (
                    <span
                      className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                      title={warnings.join(", ")}
                      data-testid={`retail-extract-warning-${idx}`}
                    >
                      review
                    </span>
                  )}
                  {dupOf && (
                    <span
                      className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200"
                      title={`Possible duplicate of "${dupOf.name}" (${dupOf.source})`}
                      data-testid={`retail-extract-duplicate-${idx}`}
                    >
                      dupe
                    </span>
                  )}
                  {row.confidence && row.confidence !== "high" && (
                    <span
                      className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200"
                      title={`Extraction confidence: ${row.confidence}`}
                    >
                      {row.confidence}
                    </span>
                  )}
                </div>
              </div>
              {/* Phase 3I.5C — Inline editable image URL (collapsed; expands when expanded). */}
              {row._selected && (
                <details className="px-3 -mt-1 mb-2 text-xs text-slate-500">
                  <summary className="cursor-pointer hover:text-slate-900">Image URL</summary>
                  <input
                    type="url"
                    placeholder="https://… (paste a product photo URL)"
                    value={row.image_url || ""}
                    onChange={(e) => editField(idx, "image_url", e.target.value)}
                    className="mt-1 w-full text-xs border border-slate-200 rounded-md px-2 py-1"
                    data-testid={`retail-extract-image-url-${idx}`}
                  />
                </details>
              )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <OwnerGuidancePanel projected={projected} />
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="text-xs text-slate-500">
          {selectedCount} of {rows.length} selected.
          {!isDraft && " Publish guard: this catalog must be Draft to commit."}
        </div>
        <button
          type="button"
          className="bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
          disabled={busy || selectedCount === 0 || !isDraft}
          onClick={submit}
          data-testid="retail-extract-commit-btn"
        >
          {busy ? "Saving…" : `Commit ${selectedCount} as drafts`}
        </button>
      </div>
    </div>
  );
}

// --- Phase 3I.5A — extraction summary card ------------------------------
function ExtractionSummaryCard({ summary, duplicates }) {
  const conf = summary.by_confidence || { high: 0, medium: 0, low: 0 };
  const dupWithin = duplicates?.duplicates_within_batch || 0;
  const dupExisting = duplicates?.duplicates_against_existing || 0;
  return (
    <div
      className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2"
      data-testid="retail-extract-summary"
    >
      <Stat label="Extracted"     value={summary.total} testId="retail-extract-summary-total" />
      <Stat label="High conf."    value={conf.high}     testId="retail-extract-summary-high" />
      <Stat label="Medium conf."  value={conf.medium}   testId="retail-extract-summary-medium" />
      <Stat label="Low conf."     value={conf.low}      testId="retail-extract-summary-low" />
      <Stat label="Need review"   value={summary.needs_review}  testId="retail-extract-summary-needs-review" tint="amber" />
      <Stat label="Missing price" value={summary.missing_price} testId="retail-extract-summary-missing-price" tint={summary.missing_price > 0 ? "rose" : null} />
      {(dupWithin + dupExisting) > 0 && (
        <div
          className="col-span-2 sm:col-span-3 lg:col-span-6 text-[11px] text-rose-700"
          data-testid="retail-extract-summary-duplicates"
        >
          Possible duplicates: {dupWithin} within this batch
          {dupExisting > 0 && `, ${dupExisting} against existing catalog`}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tint, testId }) {
  const colors = {
    amber: "text-amber-700",
    rose:  "text-rose-700",
  };
  return (
    <div className="flex flex-col">
      <div className="uppercase tracking-wide text-[10px] text-slate-500">{label}</div>
      <div className={"text-sm font-semibold " + (colors[tint] || "text-slate-900")} data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

// --- Phase 3I.5A — projected quality banner ----------------------------
function ProjectedQualityBanner({ projected, loading }) {
  if (!projected) return null;
  const { current, projected: proj, applied_count } = projected;
  if (current?.vertical_unsupported || proj?.vertical_unsupported) return null;
  const improving = (proj?.score || 0) > (current?.score || 0);
  const tone = improving ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50";
  const arrow = improving ? "↑" : "→";
  return (
    <div
      className={"border rounded-lg px-3 py-2 text-xs " + tone}
      data-testid="retail-extract-projected-quality"
    >
      <div className="font-medium text-slate-900">
        Committing {applied_count} item{applied_count === 1 ? "" : "s"} would move quality
        {" "}
        <span data-testid="retail-extract-projected-current-grade">{current?.grade || "—"}</span>
        {" "}({current?.score ?? 0}/100) {arrow}{" "}
        <span data-testid="retail-extract-projected-grade">{proj?.grade || "—"}</span>
        {" "}({proj?.score ?? 0}/100)
        {loading && <span className="ml-2 text-slate-400">recalculating…</span>}
      </div>
      {Array.isArray(proj?.tips) && proj.tips.length > 0 && (
        <div className="text-slate-600 mt-1 line-clamp-2" title={proj.tips.join(" • ")}>
          Top gap: {proj.tips[0]}
        </div>
      )}
    </div>
  );
}

// --- Phase 3I.5A — owner guidance panel --------------------------------
function OwnerGuidancePanel({ projected }) {
  // Friendly, contextual onboarding tips. We pull at most 2 from the
  // projected quality snapshot so guidance reflects the real catalog,
  // and pad with the evergreen restaurant-template tips.
  const tipsFromProjection = (projected?.projected?.tips || []).slice(0, 2);
  const evergreen = [
    "Review prices before publishing — customers see the price you set.",
    "Add allergens for safer customer filtering and clearer assistant answers.",
    "Short descriptions (1–2 sentences) noticeably improve assistant responses.",
    "Images are optional, but photos meaningfully improve customer engagement.",
  ];
  const tips = [...tipsFromProjection, ...evergreen].slice(0, 4);
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1"
      data-testid="retail-owner-guidance"
    >
      <div className="uppercase tracking-wide text-[10px] text-slate-500 mb-1">Before you publish</div>
      <ul className="list-disc pl-5 space-y-1">
        {tips.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </div>
  );
}

// ----------------------- PDF extraction panel ----------------------------

function ExtractFromPdfPanel({ catalog, isDraft, onCommitted }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);   // { products, notes, source }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setPreview(null); setError(""); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setError(""); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(
        `/retail/catalogs/${catalog.id}/extract/pdf`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreview(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not read that PDF.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onCommit = async (products) => {
    setBusy(true); setError("");
    try {
      await api.post(`/retail/catalogs/${catalog.id}/extract/commit`, { products });
      reset();
      await onCommitted();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.hint || "Commit failed."));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="retail-extract-pdf-panel">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          onChange={onFile}
          disabled={busy}
          className="text-sm"
          data-testid="retail-extract-pdf-file"
        />
        {preview && (
          <button
            type="button"
            onClick={reset}
            className="text-xs text-slate-500 underline-offset-2 hover:underline"
          >
            Choose a different file
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Upload your existing PDF menu. We&apos;ll extract items as DRAFT products
        — review each row before publishing. Unknown prices, allergens, and
        calories stay blank; we never invent missing values.
      </p>
      {error && <div className="text-xs text-rose-600" data-testid="retail-extract-pdf-error">{error}</div>}
      {preview?.notes?.length > 0 && (
        <ul className="text-xs text-slate-500 list-disc pl-5" data-testid="retail-extract-pdf-notes">
          {preview.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
      {preview && (
        <ExtractionPreviewTable
          products={normaliseExtractionRows(preview)}
          preview={preview}
          onCommit={onCommit}
          busy={busy}
          isDraft={isDraft}
          catalogId={catalog.id}
        />
      )}
    </div>
  );
}

// ----------------------- URL extraction panel ----------------------------

function ExtractFromUrlPanel({ catalog, isDraft, onCommitted }) {
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setPreview(null); setError(""); };

  const runExtract = async () => {
    if (!url.trim()) return;
    setBusy(true); setError(""); setPreview(null);
    try {
      const r = await api.post(`/retail/catalogs/${catalog.id}/extract/url`, { url: url.trim() });
      setPreview(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not fetch that URL.");
    } finally { setBusy(false); }
  };

  const onCommit = async (products) => {
    setBusy(true); setError("");
    try {
      await api.post(`/retail/catalogs/${catalog.id}/extract/commit`, { products });
      reset();
      setUrl("");
      await onCommitted();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.hint || "Commit failed."));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="retail-extract-url-panel">
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-store.com/menu"
          className="flex-1 text-sm border border-slate-200 rounded-md px-3 py-2"
          data-testid="retail-extract-url-input"
        />
        <button
          type="button"
          onClick={runExtract}
          disabled={busy || !url.trim()}
          className="bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
          data-testid="retail-extract-url-fetch"
        >
          {busy ? "Fetching…" : "Extract"}
        </button>
      </div>
      <p className="text-xs text-slate-500">
        We&apos;ll scan the page for menu items or products and return them as DRAFT
        products. Review each row before publishing — best-effort
        extraction may miss items or mislabel categories.
      </p>
      {error && <div className="text-xs text-rose-600" data-testid="retail-extract-url-error">{error}</div>}
      {preview?.notes?.length > 0 && (
        <ul className="text-xs text-slate-500 list-disc pl-5" data-testid="retail-extract-url-notes">
          {preview.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
      {preview && (
        <ExtractionPreviewTable
          products={normaliseExtractionRows(preview)}
          preview={preview}
          onCommit={onCommit}
          busy={busy}
          isDraft={isDraft}
          catalogId={catalog.id}
        />
      )}
    </div>
  );
}

// ----------------------- import wizard -----------------------------------

function ImportWizard({ catalog, onCommitted }) {
  const fileRef = useRef(null);
  const [detect, setDetect] = useState(null);     // { upload_token, headers, sample_rows, suggested_mapping, prior_mapping, canonical_fields, required_fields, row_count }
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setDetect(null); setMapping({}); setPreview(null); setError(""); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setError(""); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(
        `/retail/catalogs/${catalog.id}/import/detect`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setDetect(r.data);
      // Prefer prior mapping if present (an operator returning to update a
      // menu they imported before keeps their column choices).
      const initial = Object.keys(r.data.prior_mapping || {}).length > 0
        ? r.data.prior_mapping
        : r.data.suggested_mapping;
      setMapping(initial || {});
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not read that file. Try CSV or XLSX with a header row.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runPreview = async () => {
    if (!detect) return;
    setBusy(true); setError("");
    try {
      const r = await api.post(
        `/retail/catalogs/${catalog.id}/import/preview`,
        { upload_token: detect.upload_token, mapping, save_mapping: true }
      );
      setPreview(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Preview failed");
    } finally { setBusy(false); }
  };

  // Product attributes (Sep 2026) — the confirmation the backend
  // requires before a spreadsheet may create Brands / Units / Product
  // Families. The PREVIEW discloses them and writes nothing; the commit
  // only creates them once the operator has ticked this box. A commit
  // sent without it is refused by the backend and the refusal is
  // surfaced verbatim below, so the two can never drift.
  const [confirmNewValues, setConfirmNewValues] = useState(false);
  const newValues = useMemo(() => ({
    brands: preview?.new_brands || [],
    units: preview?.new_units || [],
    families: preview?.new_families || [],
  }), [preview]);
  const hasNewValues =
    newValues.brands.length + newValues.units.length + newValues.families.length > 0;

  const runCommit = async () => {
    if (!detect) return;
    setBusy(true); setError("");
    try {
      await api.post(
        `/retail/catalogs/${catalog.id}/import/commit`,
        {
          upload_token: detect.upload_token, mapping,
          skip_invalid: true, save_mapping: true,
          confirm_new_values: confirmNewValues,
        }
      );
      reset();
      setConfirmNewValues(false);
      await onCommitted();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "import_introduces_new_values") {
        setError(d.message || "This file introduces values that are not in your lists yet.");
      } else {
        setError(typeof d === "string" ? d : JSON.stringify(d));
      }
    } finally { setBusy(false); }
  };

  const required = useMemo(() => detect?.required_fields || ["name", "price"], [detect]);
  const canonical = detect?.canonical_fields || [];
  const requiredOk = required.every((r) => Object.values(mapping).includes(r));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="retail-import-wizard">
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-sm flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-slate-500" /> Import products
        </div>
        {detect && (
          <button
            onClick={reset}
            className="text-xs text-slate-500 hover:text-slate-900"
            data-testid="retail-import-reset"
          >
            Start over
          </button>
        )}
      </div>

      {!detect ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center" data-testid="retail-import-uploader">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={onFile}
            className="hidden"
            data-testid="retail-import-file-input"
            id={`retail-import-file-${catalog.id}`}
          />
          <label
            htmlFor={`retail-import-file-${catalog.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm cursor-pointer hover:bg-slate-800"
          >
            <Upload className="w-3.5 h-3.5" /> Choose CSV / XLSX
          </label>
          <p className="text-xs text-slate-500 mt-3">
            We&apos;ll detect your columns automatically. Required fields: name, price.
          </p>
          {busy && <div className="text-xs text-slate-500 mt-3"><Loader2 className="inline w-3 h-3 animate-spin"/> Reading file…</div>}
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500">
            {detect.filename} · {detect.row_count} rows · {detect.source_type.toUpperCase()}
          </div>

          <div>
            <div className="text-xs font-medium text-slate-700 mb-2">Map your columns</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="retail-mapping-grid">
              {detect.headers.map((h) => {
                const mapped = mapping[h];
                const isCustom = typeof mapped === "string" && mapped.startsWith("extra.");
                return (
                <div key={h} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0 truncate text-sm">{h}</div>
                  <select
                    data-testid={`retail-map-${h}`}
                    value={mapped || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMapping((m) => {
                        const next = { ...m };
                        for (const k of Object.keys(next)) if (next[k] === v) delete next[k];
                        if (v) next[h] = v; else delete next[h];
                        return next;
                      });
                    }}
                    className="h-8 rounded-md border border-slate-200 bg-white text-xs px-1"
                  >
                    <option value="">— ignore —</option>
                    {canonical.map((f) => <option key={f} value={f}>{f}</option>)}
                    {isCustom && <option value={mapped}>{mapped}</option>}
                  </select>
                  {!mapped && (
                    <button
                      type="button"
                      data-testid={`retail-map-custom-${h}`}
                      title={`Create a custom column from "${h}"`}
                      onClick={async () => {
                        try {
                          const r = await api.post(`/retail/catalogs/${catalog.id}/schema/columns`, {
                            label: h.slice(0, 60),
                            type: "text",
                          });
                          const newKey = r.data?.column?.key;
                          if (newKey) {
                            setMapping((m) => ({ ...m, [h]: `extra.${newKey}` }));
                          }
                        } catch (err) {
                          toast.error(err?.response?.data?.detail?.error || "Could not create custom column");
                        }
                      }}
                      className="text-[10px] uppercase tracking-wide px-2 py-1 rounded border border-slate-300 hover:bg-white text-slate-600"
                    >
                      + custom
                    </button>
                  )}
                </div>
              );})}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {requiredOk ? (
                <span className="text-emerald-700"><CheckCircle2 className="inline w-3 h-3"/> Required fields mapped</span>
              ) : (
                <span className="text-amber-700"><AlertTriangle className="inline w-3 h-3"/> Map at least: {required.join(", ")}</span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Tip: use <span className="font-medium">+ custom</span> on any unmapped column to create a new schema field for it (e.g. Spice level, Portion size).
            </p>
          </div>

          {hasNewValues && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2"
              data-testid="retail-import-new-values"
            >
              <div className="text-sm font-medium text-amber-900">
                This file adds values that are not in your lists yet
              </div>
              {[["Brands", newValues.brands], ["Units", newValues.units],
                ["Product families", newValues.families]].map(([title, list]) => (
                list.length ? (
                  <div key={title} className="text-xs text-amber-900/90">
                    <span className="font-semibold">{title}:</span> {list.join(", ")}
                  </div>
                ) : null
              ))}
              <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer min-h-[44px] items-center">
                <input
                  type="checkbox"
                  data-testid="retail-import-confirm-new-values"
                  checked={confirmNewValues}
                  onChange={(e) => setConfirmNewValues(e.target.checked)}
                  className="w-5 h-5 mt-0.5"
                />
                <span>Create these values when I import. Nothing has been created yet.</span>
              </label>
            </div>
          )}

          {error && <div className="text-sm text-red-600" data-testid="retail-import-error">{error}</div>}

          <div className="flex items-center gap-2">
            <button
              data-testid="retail-import-preview-btn"
              disabled={busy || !requiredOk}
              onClick={runPreview}
              className="h-9 px-3 rounded-lg bg-white border border-slate-300 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {busy ? "Working…" : "Preview"}
            </button>
            <button
              title={hasNewValues && !confirmNewValues
                ? "Confirm the new Brands / Units / Product families above first"
                : undefined}
              data-testid="retail-import-commit-btn"
              disabled={busy || !preview || (hasNewValues && !confirmNewValues)}
              onClick={runCommit}
              className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? "Working…" : `Commit ${preview?.valid_count ?? ""} products`.trim()}
            </button>
          </div>

          {preview && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs" data-testid="retail-import-preview">
              <div>
                <strong>{preview.valid_count}</strong> valid · <strong>{preview.invalid_count}</strong> invalid · {preview.total} total
              </div>
              {preview.invalid_count > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-slate-600">Show invalid rows</summary>
                  <pre className="text-[10px] mt-2 max-h-40 overflow-auto">
                    {JSON.stringify(preview.sample_invalid, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ----------------------- Retail Catalog Schema panel ----------------------
// Phase 3I.5B Slice 1. Lets the owner view & manage the per-catalog
// schema (default columns from the active vertical template plus any
// custom columns). System columns (product_name, unit_price) are
// locked. Custom columns can be added, edited, hidden, soft-deleted,
// reordered. Per-product editing of custom columns lands in Slice 2.
// UI naming intentionally generic — "Retail Catalog Schema". The
// Phase-1 default content is the restaurant template; future verticals
// will ship their own seeds.

// Product attributes (Sep 2026) — the per-catalog switches. Defaults
// are chosen so an existing catalog looks and behaves exactly as it did
// before this feature: brand / pack / family OFF, dietary badges ON
// (the storefront already renders them), Spice Level ON only for a
// restaurant catalog, Search ON.
const ATTRIBUTE_SWITCHES = [
  { key: "brand_enabled", label: "Brand",
    help: "Show a Brand field on products and let customers browse by brand." },
  { key: "pack_enabled", label: "Package size & unit",
    help: "Sell the same product in several sizes — 12 oz, 2 L, 10 kg." },
  { key: "family_enabled", label: "Product family",
    help: "Group sizes and brands of the same thing so customers can browse by product." },
  { key: "dietary_enabled", label: "Dietary badges",
    help: "Show the green VEGAN badge on customer cards." },
  { key: "spice_enabled", label: "Spice level",
    help: "Restaurant menus: mark a dish Low, Medium or Hot." },
  { key: "search_enabled", label: "Customer search",
    help: "Sticky search box with suggestions on the storefront." },
];

function ProductAttributesPanel({ catalog, onChanged }) {
  const [config, setConfig] = useState(catalog?.attribute_config || {});
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => { setConfig(catalog?.attribute_config || {}); }, [catalog]);

  const toggle = async (key, next) => {
    setBusyKey(key);
    const optimistic = { ...config, [key]: next };
    setConfig(optimistic);
    try {
      const r = await api.put(`/retail/catalogs/${catalog.id}`, {
        attribute_config: { [key]: next },
      });
      setConfig(r.data?.catalog?.attribute_config || optimistic);
      onChanged && onChanged();
    } catch (err) {
      setConfig(config);
      toast.error(err?.response?.data?.detail?.message || "Could not save that setting.");
    } finally { setBusyKey(null); }
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-3"
      data-testid="retail-product-attributes-panel"
    >
      <header>
        <h3 className="text-base font-semibold text-slate-900">Product attributes</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Turn on only what this catalog needs. Everything is off by default, so a
          simple menu stays simple.
        </p>
      </header>
      <div className="grid sm:grid-cols-2 gap-2">
        {ATTRIBUTE_SWITCHES.map((sw) => {
          const on = !!config[sw.key];
          return (
            <label
              key={sw.key}
              className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50"
              data-testid={`retail-attr-switch-${sw.key}`}
            >
              <input
                type="checkbox"
                className="w-5 h-5 mt-0.5"
                checked={on}
                disabled={busyKey === sw.key}
                onChange={(e) => toggle(sw.key, e.target.checked)}
                data-testid={`retail-attr-switch-input-${sw.key}`}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800">{sw.label}</span>
                <span className="block text-[11px] text-slate-500">{sw.help}</span>
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
}

// Tenant-level dictionaries. Shared by EVERY catalog this tenant owns —
// duplicating a catalog never forks them.
const DICTIONARY_TABS = [
  { kind: "brand", title: "Brands", noun: "brand" },
  { kind: "unit", title: "Units", noun: "unit" },
  { kind: "family", title: "Product families", noun: "product family" },
];

function AttributeDictionaryPanel({ catalog }) {
  const [kind, setKind] = useState("brand");
  const [values, setValues] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");

  const load = async (nextKind = kind, inactive = showInactive) => {
    setLoading(true);
    try {
      const { data } = await api.get("/retail/attribute-values", {
        params: { kind: nextKind, include_inactive: inactive },
      });
      setValues(data?.values || []);
    } catch (_e) {
      setValues([]);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(kind, showInactive); }, [kind, showInactive]);

  const add = async () => {
    const candidate = draft.trim();
    if (!candidate) return;
    try {
      const { data } = await api.post("/retail/attribute-values", { kind, value: candidate });
      setDraft("");
      if (data?.created === false) {
        toast.info(`“${data.value.value}” is already in the list.`);
      } else {
        toast.success(`Added “${data.value.value}”.`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not add that value.");
    }
  };

  const rename = async (row) => {
    const next = editText.trim();
    setEditing(null);
    if (!next || next === row.value) return;
    setBusyId(row.id);
    try {
      await api.patch(`/retail/attribute-values/${row.id}`, { value: next }, { params: { kind } });
      toast.success("Renamed. Products using it were updated.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not rename that value.");
    } finally { setBusyId(null); }
  };

  const setActive = async (row, active) => {
    setBusyId(row.id);
    try {
      await api.patch(`/retail/attribute-values/${row.id}`, { active }, { params: { kind } });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not update that value.");
    } finally { setBusyId(null); }
  };

  const noun = DICTIONARY_TABS.find((t) => t.kind === kind)?.noun || kind;

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-3"
      data-testid="retail-attribute-dictionary-panel"
    >
      <header>
        <h3 className="text-base font-semibold text-slate-900">Brands, units &amp; product families</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Shared across every catalog in this account. Deactivating a value hides it
          from the pickers — products already using it keep showing it.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {DICTIONARY_TABS.map((t) => (
          <button
            key={t.kind}
            type="button"
            onClick={() => setKind(t.kind)}
            data-testid={`retail-dictionary-tab-${t.kind}`}
            className={
              "h-10 px-3 rounded-lg text-sm font-medium border " +
              (kind === t.kind
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300")
            }
          >
            {t.title}
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-2 text-xs text-slate-600 h-10">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="retail-dictionary-show-inactive"
          />
          Show inactive
        </label>
      </div>

      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={`Add a ${noun}…`}
          data-testid="retail-dictionary-new-value"
          className="flex-1 h-11 rounded-lg border border-slate-300 px-3 text-sm"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          data-testid="retail-dictionary-add"
          className="h-11 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-50"
        >
          Add
        </button>
      </div>

      {loading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : values.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="retail-dictionary-empty">
          No {noun} values yet.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
          {values.map((row) => (
            <li
              key={row.id}
              className="flex items-center gap-2 px-3 py-2 min-h-[48px]"
              data-testid={`retail-dictionary-row-${row.id}`}
            >
              {editing === row.id ? (
                <input
                  autoFocus
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onBlur={() => rename(row)}
                  onKeyDown={(e) => { if (e.key === "Enter") rename(row); if (e.key === "Escape") setEditing(null); }}
                  data-testid={`retail-dictionary-rename-${row.id}`}
                  className="flex-1 h-9 rounded-md border border-slate-300 px-2 text-sm"
                />
              ) : (
                <span className={"flex-1 text-sm truncate " + (row.active ? "" : "text-slate-400 line-through")}>
                  {row.value}
                  {row.system && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">built-in</span>
                  )}
                </span>
              )}
              {!row.system && editing !== row.id && (
                <button
                  type="button"
                  onClick={() => { setEditing(row.id); setEditText(row.value); }}
                  disabled={busyId === row.id}
                  data-testid={`retail-dictionary-edit-${row.id}`}
                  className="h-9 px-2 text-xs text-slate-600 hover:text-slate-900"
                >
                  Rename
                </button>
              )}
              <button
                type="button"
                onClick={() => setActive(row, !row.active)}
                disabled={busyId === row.id}
                data-testid={`retail-dictionary-toggle-${row.id}`}
                className="h-9 px-2 text-xs text-slate-600 hover:text-slate-900"
              >
                {row.active ? "Deactivate" : "Reactivate"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RetailCatalogSchemaPanel({ catalog }) {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    label: "", type: "text", options: "", required: false,
  });
  const [busyKey, setBusyKey] = useState(null);
  // Phase 3L.1 Pass B — in-app confirm replaces window.confirm.
  const [deleteCol, setDeleteCol] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/retail/catalogs/${catalog.id}/schema`);
      setSchema(r.data?.schema || null);
    } catch { setSchema(null); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [catalog.id]);

  const addColumn = async () => {
    if (!draft.label.trim()) return;
    const needsOptions = draft.type === "dropdown" || draft.type === "multi_select";
    const opts = draft.options.split(",").map((s) => s.trim()).filter(Boolean);
    if (needsOptions && opts.length === 0) {
      toast.error("Add at least one option (comma-separated).");
      return;
    }
    try {
      await api.post(`/retail/catalogs/${catalog.id}/schema/columns`, {
        label: draft.label.trim(),
        type: draft.type,
        required: draft.required,
        options: opts,
      });
      setDraft({ label: "", type: "text", options: "", required: false });
      setAdding(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || "Could not add column");
    }
  };

  const setVisible = async (col, visible) => {
    setBusyKey(col.key);
    try {
      await api.patch(`/retail/catalogs/${catalog.id}/schema/columns/${col.key}`, { visible });
      await load();
    } finally { setBusyKey(null); }
  };

  const deleteColumn = (col) => {
    if (col.system) return;
    // Phase 3L.1 Pass B — open in-app confirm.
    setDeleteCol(col);
  };

  const doDeleteColumn = async () => {
    const col = deleteCol;
    if (!col) return;
    setDeleteCol(null);
    setBusyKey(col.key);
    try {
      await api.delete(`/retail/catalogs/${catalog.id}/schema/columns/${col.key}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.hint || "Could not delete column");
    } finally { setBusyKey(null); }
  };

  if (loading || !schema) {
    return (
      <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid="retail-schema-panel">
        <h3 className="text-base font-semibold text-slate-900">Retail Catalog Schema</h3>
        <p className="text-xs text-slate-500 mt-2">{loading ? "Loading schema…" : "Schema unavailable."}</p>
      </section>
    );
  }

  const columns = (schema.columns || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const activeColumns = columns.filter((c) => c.active !== false);
  const hiddenColumns = columns.filter((c) => c.active === false);

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-4" data-testid="retail-schema-panel">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Retail Catalog Schema</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Default columns for this catalog (Phase 1 template:
            {" "}<span className="font-mono">{schema.template_vertical || "—"}</span>).
            Add your own custom columns — system columns can&apos;t be deleted.
          </p>
        </div>
        <div className="flex gap-2">
          <SchemaPresetMenu
            catalogId={catalog.id}
            existingKeys={new Set(columns.map((c) => c.key))}
            onAdded={load}
          />
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50"
            data-testid="retail-schema-add-toggle"
          >
            {adding ? "Cancel" : "+ Add column"}
          </button>
        </div>
      </header>
      {adding && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="retail-schema-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              className="text-sm border border-slate-200 rounded-md px-2 py-1"
              placeholder="Column label (e.g. Spice level)"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              data-testid="retail-schema-add-label"
              maxLength={60}
            />
            <select
              className="text-sm border border-slate-200 rounded-md px-2 py-1"
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value })}
              data-testid="retail-schema-add-type"
            >
              <option value="text">Text</option>
              <option value="long_text">Long text</option>
              <option value="number">Number</option>
              <option value="currency">Currency</option>
              <option value="yes_no">Yes / No</option>
              <option value="dropdown">Dropdown</option>
              <option value="multi_select">Multi-select tags</option>
              <option value="date">Date</option>
              <option value="time">Time</option>
              <option value="image_url">Image URL</option>
            </select>
          </div>
          {(draft.type === "dropdown" || draft.type === "multi_select") && (
            <input
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1"
              placeholder="Options (comma-separated, e.g. Mild, Medium, Hot)"
              value={draft.options}
              onChange={(e) => setDraft({ ...draft, options: e.target.value })}
              data-testid="retail-schema-add-options"
            />
          )}
          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={draft.required}
                onChange={(e) => setDraft({ ...draft, required: e.target.checked })}
                className="w-3.5 h-3.5 accent-slate-900"
              />
              Required
            </label>
            <button
              type="button"
              onClick={addColumn}
              className="bg-slate-900 text-white text-sm font-medium px-3 py-1.5 rounded-md"
              data-testid="retail-schema-add-submit"
            >
              Add column
            </button>
          </div>
        </div>
      )}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-left bg-slate-50">
            <tr>
              <th className="py-2 px-3">Column</th>
              <th className="py-2 px-3">Type</th>
              <th className="py-2 px-3">Required</th>
              <th className="py-2 px-3">Visibility</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {activeColumns.map((c) => (
              <tr key={c.key} className="border-t border-slate-100" data-testid={`retail-schema-column-${c.key}`}>
                <td className="py-2 px-3">
                  <div className="font-medium text-slate-900">{c.label}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{c.key}{c.system && " · system"}</div>
                </td>
                <td className="py-2 px-3 text-slate-600 text-xs">{c.type}</td>
                <td className="py-2 px-3">
                  {c.required ? <span className="text-rose-700 text-xs">Required</span> : <span className="text-slate-500 text-xs">Optional</span>}
                </td>
                <td className="py-2 px-3">
                  <button
                    type="button"
                    disabled={busyKey === c.key}
                    onClick={() => setVisible(c, !c.visible)}
                    className="text-xs underline-offset-2 hover:underline text-slate-600"
                    data-testid={`retail-schema-visibility-${c.key}`}
                  >
                    {c.visible ? "Visible" : "Hidden"}
                  </button>
                </td>
                <td className="py-2 px-3 text-right">
                  {c.system ? (
                    <span className="text-[10px] uppercase tracking-wide text-slate-400" data-testid={`retail-schema-system-lock-${c.key}`}>
                      system · locked
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={busyKey === c.key}
                      onClick={() => deleteColumn(c)}
                      className="text-xs text-rose-700 hover:underline underline-offset-2"
                      data-testid={`retail-schema-delete-${c.key}`}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hiddenColumns.length > 0 && (
        <details className="text-xs text-slate-500" data-testid="retail-schema-hidden-list">
          <summary className="cursor-pointer hover:text-slate-900">
            {hiddenColumns.length} hidden / removed column{hiddenColumns.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-2 list-disc pl-5">
            {hiddenColumns.map((c) => (
              <li key={c.key}>
                <span className="font-medium">{c.label}</span>{" "}
                <span className="font-mono text-[10px]">({c.key})</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      <ConfirmModal
        open={Boolean(deleteCol)}
        title="Hide custom column?"
        message={deleteCol ? `"${deleteCol.label}" will be hidden from this catalog's schema. Existing product data is preserved — you can re-add the column later to surface it again.` : ""}
        confirmLabel="Hide column"
        tone="warning"
        onCancel={() => setDeleteCol(null)}
        onConfirm={doDeleteColumn}
        testIdPrefix="retail-schema-delete-confirm"
      />
    </section>
  );
}

// ----------------------- product table -----------------------------------

function SchemaPresetMenu({ catalogId, existingKeys, onAdded }) {
  // Phase 3I.5B Slice 2 — small "Start from a common field" affordance.
  // Restaurant-only presets for Phase 1. Future verticals will ship
  // their own preset lists; do NOT generalise into a marketplace.
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get(`/retail/catalogs/${catalogId}/schema/presets`)
      .then((r) => setPresets(r.data?.presets || []))
      .catch(() => setPresets([]));
  }, [open, catalogId]);
  const add = async (p) => {
    if (existingKeys.has(p.preset)) {
      alert(`A column with key "${p.preset}" already exists.`);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/retail/catalogs/${catalogId}/schema/columns`, {
        key: p.preset,
        label: p.label,
        type: p.type,
        options: p.options || [],
        required: !!p.required,
      });
      await onAdded();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error || "Could not add preset");
    } finally { setBusy(false); }
  };
  return (
    <div className="relative" data-testid="retail-schema-presets">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50"
        data-testid="retail-schema-presets-toggle"
      >
        ✦ Start from a common field
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-72 bg-white border border-slate-200 rounded-md shadow-lg p-2 space-y-1">
          {presets.length === 0 && (
            <div className="text-xs text-slate-500 p-2">No presets for this vertical yet.</div>
          )}
          {presets.map((p) => {
            const exists = existingKeys.has(p.preset);
            return (
              <button
                key={p.preset}
                type="button"
                disabled={busy || exists}
                onClick={() => add(p)}
                className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`retail-schema-preset-${p.preset}`}
              >
                <div className="font-medium text-slate-900">{p.label}</div>
                <div className="text-[10px] text-slate-500">
                  {p.type}{p.options?.length ? ` · ${p.options.join(" / ")}` : ""}
                  {exists && " · already added"}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ----------------------- product edit modal (Slice 2) ---------------------
// Schema-driven per-product editor. Reads the active schema and renders
// the right input per column type. System fields edit the canonical
// product field; custom columns POST to `fields.{key}`.
// ----------------------- shared Parcel Shipping product fields -----------
// Parcel Shipping Readiness & Checkout Completion — Phase 3. One shared
// component + field-definition path for both Add and Edit product forms
// (previously Edit-only; Add exposed no parcel fields at all). Mirrors
// `backend/parcel_shipping/models.py::compute_metadata_complete` exactly
// so the "Ready" hint here never disagrees with what the backend will
// actually persist as `metadata_complete`.

const DEFAULT_PARCEL = {
  eligible: false, weight: "", weightUnit: "lb",
  length: "", width: "", height: "", dimUnit: "in",
};

// Seeds parcel state from a product's persisted `shipping` subdoc.
// `??` (not `||`) so a genuine 0 value is preserved rather than blanked.
function seedParcelFromProduct(product) {
  return {
    eligible: !!product?.shipping?.parcel_shipping_eligible,
    weight: product?.shipping?.weight ?? "",
    weightUnit: product?.shipping?.weight_unit || "lb",
    length: product?.shipping?.length ?? "",
    width: product?.shipping?.width ?? "",
    height: product?.shipping?.height ?? "",
    dimUnit: product?.shipping?.dim_unit || "in",
  };
}

// Mirrors compute_metadata_complete: eligible + all four dims strictly > 0
// (a "0" string is truthy in JS but backend rejects it — check numerically).
function parcelIsComplete(parcel) {
  if (!parcel.eligible) return false;
  return ["weight", "length", "width", "height"].every((k) => {
    const n = Number(parcel[k]);
    return parcel[k] !== "" && !isNaN(n) && n > 0;
  });
}

// True only when the operator has actually touched parcel data — used to
// decide whether the separate parcel-shipping PUT is worth calling.
function parcelHasAnyData(parcel) {
  return !!(parcel.eligible || parcel.weight || parcel.length || parcel.width || parcel.height);
}

function parcelToApiPayload(parcel) {
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  return {
    parcel_shipping_eligible: parcel.eligible,
    weight: num(parcel.weight),
    weight_unit: parcel.weight ? parcel.weightUnit : null,
    length: num(parcel.length),
    width: num(parcel.width),
    height: num(parcel.height),
    dim_unit: (parcel.length || parcel.width || parcel.height) ? parcel.dimUnit : null,
  };
}

function ParcelShippingFields({ value: parcel, onChange: setParcel }) {
  return (
    <div className="pt-3 border-t border-slate-200" data-testid="product-parcel-shipping-section">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Parcel Shipping</div>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid="product-parcel-eligible-toggle"
          checked={parcel.eligible}
          onChange={(e) => setParcel((p) => ({ ...p, eligible: e.target.checked }))}
          className="h-4 w-4 cursor-pointer accent-indigo-600"
        />
        <span className="text-sm text-slate-700">Shippable via Parcel Shipping</span>
      </label>
      {parcel.eligible && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-medium text-slate-600">Weight</label>
              <input
                type="number" min="0" step="0.01"
                data-testid="product-parcel-weight"
                value={parcel.weight}
                onChange={(e) => setParcel((p) => ({ ...p, weight: e.target.value }))}
                className="mt-0.5 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600">Weight unit</label>
              <select
                data-testid="product-parcel-weight-unit"
                value={parcel.weightUnit}
                onChange={(e) => setParcel((p) => ({ ...p, weightUnit: e.target.value }))}
                className="mt-0.5 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
              >
                {["lb", "oz", "kg", "g"].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="text-[11px] font-medium text-slate-600">Length</label>
              <input type="number" min="0" step="0.01" data-testid="product-parcel-length"
                value={parcel.length} onChange={(e) => setParcel((p) => ({ ...p, length: e.target.value }))}
                className="mt-0.5 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600">Width</label>
              <input type="number" min="0" step="0.01" data-testid="product-parcel-width"
                value={parcel.width} onChange={(e) => setParcel((p) => ({ ...p, width: e.target.value }))}
                className="mt-0.5 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600">Height</label>
              <input type="number" min="0" step="0.01" data-testid="product-parcel-height"
                value={parcel.height} onChange={(e) => setParcel((p) => ({ ...p, height: e.target.value }))}
                className="mt-0.5 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5" />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-600">Unit</label>
              <select
                data-testid="product-parcel-dim-unit"
                value={parcel.dimUnit}
                onChange={(e) => setParcel((p) => ({ ...p, dimUnit: e.target.value }))}
                className="mt-0.5 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
              >
                {["in", "cm"].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div className="text-[11px]" data-testid="product-parcel-complete-hint">
            {parcelIsComplete(parcel)
              ? <span className="text-emerald-600">Ready — counts toward this catalog's shippable products.</span>
              : <span className="text-amber-600">Weight and all three dimensions are required (greater than zero) before this product counts as shippable.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function ProductEditModal({ catalog, product, onClose, onSaved, onImageChange }) {
  const [schema, setSchema] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({});
  const [err, setErr] = useState("");
  // Product photo (Sep 2026). Held OUTSIDE `draft` deliberately: the
  // image has its own endpoints and its own success/failure, so a failed
  // upload must not be able to disturb anything the tenant has typed
  // into the form. Seeded from the product and updated in place by
  // `ProductImageUploader`, so the modal reflects the saved state
  // without a refetch. Same "system-adjacent concern, separate
  // endpoint" reasoning as the Clover mapping and parcel block below.
  const [imageUrl, setImageUrl] = useState(product?.image_url || "");
  useEffect(() => { setImageUrl(product?.image_url || ""); }, [product]);
  // Clover POS Integration — Milestone 1 (Feb 2026). System-level POS
  // metadata (Clover inventory item id) lives OUTSIDE the schema-driven
  // custom column pipeline so it's clearly distinguished from tenant
  // catalog fields. Empty string = clear mapping (sent as null).
  const [cloverItemId, setCloverItemId] = useState("");

  // Walk-in customer recognition/verification correction (Aug 2026,
  // round 3 — Product Owner review). SKU lives OUTSIDE the schema-
  // driven custom column pipeline, same reasoning as the Clover
  // mapping above: it's a system-adjacent identity field
  // (`retail/catalog.py::STABLE_IDENTITY_ENABLED_FIELD`/
  // `sku_normalized`) owned by the stable-product-identity contract,
  // not a tenant-configurable catalog column. `ManualProductModal`
  // already collects it at CREATE time; this is the corresponding
  // EDIT-time field for a product that was imported/created without
  // one — the only way today to give an already-existing product a
  // stable identity short of a full re-import.
  const [sku, setSku] = useState("");

  // FedEx Parcel Shipping Completion (Phase 6) — per-product parcel
  // metadata. Lives outside the schema-driven custom column pipeline,
  // same as the Clover mapping above: it's a system-adjacent concern
  // owned by a different backend module (`parcel_shipping`), saved via
  // its own dedicated endpoint (PUT /commerce/parcel-shipping/products/
  // {id}), not the generic catalog product PATCH.
  const [parcel, setParcel] = useState(DEFAULT_PARCEL);

  // Product tax treatment (Oct 2026) — Use catalog default / Tax exempt /
  // Custom rate. Outside the schema pipeline like the sections around it,
  // and sent inside the one product PATCH (only when the choice changed),
  // so the backend writes it in the same update as everything else.
  const [taxTreatment, setTaxTreatment] = useState(() => taxTreatmentFromProduct(product));
  const [taxError, setTaxError] = useState("");

  // Product attributes (Sep 2026) — Brand / Product Family / Package
  // Size / Unit / Vegan / Spice Level, in the SAME shared block the
  // Add-product form uses. Which fields render is decided by this
  // catalog's own `attribute_config`, so a catalog with the defaults
  // shows nothing new at all.
  const attributeConfig = catalog?.attribute_config || {};
  const [attrs, setAttrs] = useState(() => attributesFromProduct(product));

  useEffect(() => {
    setAttrs(attributesFromProduct(product));
  }, [product]);

  useEffect(() => {
    api.get(`/retail/catalogs/${catalog.id}/schema`).then((r) => {
      setSchema(r.data?.schema || null);
      // Seed draft from product values.
      const cols = (r.data?.schema?.columns || []).filter((c) => c.active !== false && c.visible);
      const seed = {};
      for (const c of cols) {
        const sf = c.storage_field || "";
        if (sf.startsWith("extra.")) {
          const ek = sf.split(".", 2)[1];
          seed[c.key] = (product.extra || {})[ek] ?? "";
        } else if (sf === "active") {
          seed[c.key] = product.active !== false ? "Active" : "Inactive";
        } else if (sf === "available") {
          seed[c.key] = product.available ? "Yes" : "No";
        } else if (sf === "price") {
          seed[c.key] = product.price ?? "";
        } else {
          seed[c.key] = product[sf] ?? "";
        }
      }
      setDraft(seed);
    });
    // Seed Clover item id from persisted mapping.
    setCloverItemId(product?.pos_mappings?.clover?.item_id || "");
    // Seed SKU from the persisted product row.
    setSku(product?.sku || "");
    // Seed parcel-shipping metadata from the product's `shipping` sub-doc.
    setParcel(seedParcelFromProduct(product));
    // Seed the tax treatment from the stored product (missing fields =
    // Use catalog default).
    setTaxTreatment(taxTreatmentFromProduct(product));
    setTaxError("");
  }, [catalog.id, product]);

  const save = async () => {
    if (!schema) return;
    // Phase 3 (Parcel Shipping) — never silently persist a
    // checked-shippable product with incomplete metadata; the operator
    // must fill in every required field before it counts as shippable.
    if (parcel.eligible && !parcelIsComplete(parcel)) {
      setErr("Weight and all three dimensions (greater than zero) are required before this product can be marked shippable.");
      return;
    }
    // Validate the tax treatment BEFORE anything is sent, so an invalid
    // custom rate saves nothing at all.
    const taxChanged = taxTreatmentChanged(taxTreatment, product);
    const taxPayload = taxChanged ? taxTreatmentPayload(taxTreatment) : null;
    if (taxPayload?.error) {
      setTaxError(taxPayload.error);
      setErr("Fix the tax rate before saving.");
      return;
    }
    setTaxError("");
    setBusy(true); setErr("");
    try {
      // Build patch: system + system-adjacent → top-level; custom → fields.
      const patch = { fields: {} };
      for (const c of schema.columns) {
        if (c.active === false || !c.visible) continue;
        const v = draft[c.key];
        const sf = c.storage_field || "";
        if (sf.startsWith("extra.")) {
          if (sf === "extra.unit") {
            patch.unit = v === "" ? null : v;
          } else {
            patch.fields[c.key] = v;
          }
        } else if (sf === "name") patch.name = v;
        else if (sf === "category") patch.category = v;
        else if (sf === "description") patch.description = v;
        else if (sf === "price") patch.price = v === "" ? null : Number(v);
        else if (sf === "available") patch.available = (String(v).toLowerCase() === "yes");
        else if (sf === "active") patch.active = (String(v).toLowerCase() === "active");
      }
      // Clover POS Integration — Milestone 1. Only send the mapping in
      // the patch when the local draft differs from what the product
      // already has persisted; keeps the PATCH minimal and legacy edits
      // untouched.
      const trimmed = String(cloverItemId || "").trim();
      const persisted = product?.pos_mappings?.clover?.item_id || "";
      if (trimmed !== persisted) {
        patch.pos_mappings = {
          clover: { item_id: trimmed ? trimmed : null },
        };
      }
      // Same "only send when changed" minimal-PATCH discipline as the
      // Clover mapping above. Sent even when trimmed to blank — the
      // backend rejects a blank SKU (400 sku_required_for_stable_
      // identity) rather than silently accepting it, and rejects any
      // change at all once Favorites already exist for this product
      // (409 sku_locked_by_existing_favorites) — both surface through
      // the same `err` display below via their `.message`.
      const trimmedSku = String(sku || "").trim();
      const persistedSku = String(product?.sku || "").trim();
      if (trimmedSku !== persistedSku) {
        patch.sku = trimmedSku;
      }
      // Product attributes are merged LAST so they always win over any
      // legacy schema column that happens to target the same concept
      // (e.g. the pre-existing free-text `extra.unit`).
      Object.assign(patch, attributesToPayload(attrs, attributeConfig));
      // Product tax treatment (Oct 2026) — part of the SAME product
      // PATCH, validated server-side by the same rules as the tax
      // endpoint and written in one update: the save is all-or-nothing,
      // so a refusal can never leave half of it stored. Only sent when
      // the choice changed.
      if (taxPayload) Object.assign(patch, taxPayload.body);
      await api.patch(`/retail/catalogs/${catalog.id}/products/${product.id}`, patch);

      // FedEx Parcel Shipping Completion (Phase 6) — separate endpoint,
      // separate backend module. Only call it when the parcel section
      // actually changed, same "minimal PATCH" discipline as Clover above.
      const persistedParcel = seedParcelFromProduct(product);
      if (JSON.stringify(parcel) !== JSON.stringify(persistedParcel)) {
        await api.put(`/commerce/parcel-shipping/products/${product.id}`, parcelToApiPayload(parcel));
      }

      if (taxPayload) {
        toast.success(`Saved. Tax treatment: ${taxTreatmentLabel(taxTreatment)}.`);
      }
      await onSaved();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d?.hint || d?.error || d?.message || (Array.isArray(d?.errors) ? d.errors.join(", ") : "Save failed"));
    } finally { setBusy(false); }
  };

  if (!schema) return null;
  const cols = schema.columns.filter((c) => c.active !== false && c.visible).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-3" data-testid="retail-product-edit-modal">
      <div className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="font-semibold text-sm">Edit product</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-sm">Close</button>
        </div>
        <div className="p-4 space-y-3">
          {cols.map((c) => (
            <FieldInput
              key={c.key}
              column={c}
              value={draft[c.key] ?? ""}
              onChange={(v) => setDraft((d) => ({ ...d, [c.key]: v }))}
            />
          ))}
          <ProductAttributesFields
            value={attrs}
            onChange={setAttrs}
            config={attributeConfig}
            vertical={catalog?.vertical}
            testIdPrefix="product-edit"
          />
          {/* Product tax treatment (Oct 2026) — its own always-visible
              section, for every catalog type and screen size; this is
              the control Commerce -> Taxes points tenants to. */}
          <ProductTaxTreatmentFields
            value={taxTreatment}
            onChange={(next) => { setTaxTreatment(next); setTaxError(""); }}
            catalogDefaultRate={catalog?.default_tax_rate}
            error={taxError}
          />
          {/* Product photo (Sep 2026) — its own section, visually
              separated from the schema-driven fields above, because it
              saves through its own endpoint the moment it uploads
              rather than on Save. `onSaved` is deliberately NOT called
              here: it would reload the catalog list and could discard
              form edits in progress. Instead `onImageChange` patches
              just this product's `image_url` in the parent's cached
              rows, so closing and reopening the editor (without Save)
              shows the saved photo rather than the stale pre-upload
              row. */}
          <ProductImageUploader
            catalogId={catalog.id}
            productId={product.id}
            imageUrl={imageUrl}
            onChange={(url) => {
              setImageUrl(url);
              onImageChange?.(product.id, url);
            }}
            testIdPrefix="product-edit-image"
          />
          {/* Returning-member/durable-identity correction (Sep 2026) —
              a product's merchant SKU no longer gates customer
              Favorites at all (every product already has a durable
              internal identity — its own id — see `services/
              favorites.py`'s own docstring). A SKU still matters for
              CSV/XLSX re-import matching: assigning one here is the
              ONE supported way, short of a full catalog re-import, to
              give an already-existing product a merchant-SKU-based
              re-import match key. The backend still locks the SKU
              (409) against change/removal once this product has a
              saved Favorite — an intentionally conservative guard,
              surfaced below via the same error banner as any other
              save failure. */}
          <div className="pt-3 border-t border-slate-200" data-testid="product-sku-section">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Product SKU (stable identity)</div>
            <label className="text-xs font-medium text-slate-700" htmlFor="retail-product-edit-sku">
              SKU
            </label>
            <input
              id="retail-product-edit-sku"
              data-testid="retail-product-edit-sku"
              type="text"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="Enter a unique SKU for this product"
              className="mt-1 w-full text-sm font-mono border border-slate-300 rounded-md px-2 py-1.5"
            />
            <div className="text-[11px] text-slate-500 mt-1" data-testid="retail-product-edit-sku-hint">
              {product?.stable_identity_enabled
                ? "This product has a merchant SKU — future re-imports of the same source will update this exact product instead of creating a duplicate."
                : "This product has no SKU yet. Customer Favorites already works without one; a SKU only helps a future re-import find and update this exact product instead of creating a duplicate."}
            </div>
          </div>
          {/* Clover POS Integration — Milestone 1. Optional per-product
              mapping. Visible on every product edit; blank = unmapped.
              Rudras product id is displayed above for clarity so the
              administrator does not confuse the two identifiers. */}
          <div className="pt-3 border-t border-slate-200" data-testid="product-pos-mapping-section">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">External POS mapping</div>
            <div className="text-[11px] text-slate-500 mb-1">Rudras product id: <span className="font-mono">{product.id}</span></div>
            <label className="text-xs font-medium text-slate-700" htmlFor="clover-item-id-input">
              Clover inventory item ID <span className="text-slate-400">(optional)</span>
            </label>
            <input
              id="clover-item-id-input"
              data-testid="product-clover-item-id-input"
              type="text"
              value={cloverItemId}
              onChange={(e) => setCloverItemId(e.target.value)}
              placeholder="Enter the matching Clover item ID"
              className="mt-1 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Only used when this tenant is on External POS + Clover. Leave blank to remove the mapping.
            </div>
          </div>
          {/* FedEx Parcel Shipping Completion (Phase 6). Feeds the
              "Shippable Products Exist" / "At Least One Catalog Opted In"
              Parcel Shipping readiness checks — a catalog can enable
              Parcel Shipping in Fulfillment Methods, but it stays
              unavailable to customers until at least one product here is
              marked shippable with complete weight + dimensions. */}
          <ParcelShippingFields value={parcel} onChange={setParcel} />
          {err && <div className="text-xs text-rose-600" data-testid="retail-product-edit-error">{err}</div>}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-md border border-slate-300">Cancel</button>
          <button onClick={save} disabled={busy} className="text-sm px-4 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50" data-testid="retail-product-edit-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldInput({ column, value, onChange }) {
  const c = column;
  const id = `retail-field-${c.key}`;
  const label = (
    <label className="text-xs font-medium text-slate-700">
      {c.label}{c.required && <span className="text-rose-600"> *</span>}
      {c.system && <span className="text-[10px] uppercase tracking-wide text-slate-400 ml-2">system</span>}
    </label>
  );
  if (c.type === "long_text") {
    return (
      <div className="space-y-1">{label}
        <textarea data-testid={id} value={value ?? ""} maxLength={c.max_length || 4000}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm border border-slate-200 rounded-md px-2 py-1 min-h-[80px]" />
      </div>
    );
  }
  if (c.type === "dropdown" || (c.type === "yes_no" && c.options?.length)) {
    const opts = c.options?.length ? c.options : (c.type === "yes_no" ? ["Yes", "No"] : []);
    return (
      <div className="space-y-1">{label}
        <select data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm border border-slate-200 rounded-md px-2 py-1">
          <option value="">— select —</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (c.type === "yes_no") {
    return (
      <div className="space-y-1">{label}
        <select data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm border border-slate-200 rounded-md px-2 py-1">
          <option value="">— select —</option>
          <option value="Yes">Yes</option><option value="No">No</option>
        </select>
      </div>
    );
  }
  if (c.type === "multi_select") {
    const arr = Array.isArray(value) ? value : (typeof value === "string" && value ? value.split(",").map((s) => s.trim()) : []);
    const opts = c.options || [];
    const toggle = (o) => {
      const has = arr.includes(o);
      onChange(has ? arr.filter((x) => x !== o) : [...arr, o]);
    };
    return (
      <div className="space-y-1">{label}
        <div data-testid={id} className="flex flex-wrap gap-1">
          {opts.length === 0 ? (
            <input
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1"
              placeholder="Comma-separated values"
              value={arr.join(", ")}
              onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            />
          ) : opts.map((o) => (
            <button key={o} type="button" onClick={() => toggle(o)}
              className={"text-xs px-2 py-1 rounded-md border " + (arr.includes(o) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300")}>
              {o}
            </button>
          ))}
        </div>
      </div>
    );
  }
  const inputType = c.type === "number" || c.type === "currency" ? "number"
    : c.type === "date" ? "date"
    : c.type === "time" ? "time"
    : "text";
  return (
    <div className="space-y-1">{label}
      <input type={inputType} data-testid={id} value={value ?? ""} maxLength={c.max_length || undefined}
        step={c.type === "currency" ? "0.01" : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1" />
      {c.help && <p className="text-[10px] text-slate-500">{c.help}</p>}
    </div>
  );
}

export function ProductTable({ catalog, refreshKey, onReload, onAddManual }) {
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);  // open product edit modal
  const [exporting, setExporting] = useState(false);

  // Product attributes (Sep 2026) — CSV export. Goes through `api` so
  // the Bearer token is attached (see the button's own comment), then
  // hands the browser a blob to save.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/retail/catalogs/${catalog.id}/products/export.csv`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(catalog.name || "catalog").replace(/\s+/g, "-").toLowerCase()}-products.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not export products.");
    } finally {
      setExporting(false);
    }
  };

  // ------ Server-side controlled state (Retail Catalog UX sprint Feb 2026) ------
  const PAGE_SIZE = 50;
  const [page, setPage]               = useState(1);
  const [sortBy, setSortBy]           = useState("name");
  const [sortDir, setSortDir]         = useState("asc");
  const [search, setSearch]           = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [data, setData]               = useState({ items: [], total: 0 });
  const [loading, setLoading]         = useState(true);
  // Photo changes made in Edit Product, each stamped with a sequence
  // number. A list request records the sequence when it STARTS; any
  // photo change newer than that happened after the server read the
  // rows, so the response's `image_url` for that product is stale and
  // the confirmed change is re-applied. See `patchRowImage` below.
  const photoEditsRef = useRef({ seq: 0, byId: new Map() });
  // Debounce the search so each keystroke doesn't fire a round-trip.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset back to page 1 whenever the operator changes search / filter /
  // sort. Without this, sorting on the second page would silently keep
  // them on an offset that may no longer have rows.
  useEffect(() => { setPage(1); }, [debouncedSearch, activeFilter, sortBy, sortDir]);

  // Single fetch effect — re-runs on every dependency. Aborts the
  // previous request when params change rapidly (typeahead, sort
  // clicks) so we never display stale rows.
  useEffect(() => {
    let aborted = false;
    setLoading(true);
    const params = {
      limit:  PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      sort_by:  sortBy,
      sort_dir: sortDir,
      search: debouncedSearch || undefined,
      active_filter: activeFilter,
    };
    const photoSeqAtStart = photoEditsRef.current.seq;
    api.get(`/retail/catalogs/${catalog.id}/products`, { params })
      .then((r) => {
        if (aborted) return;
        const edits = photoEditsRef.current.byId;
        const items = (r.data.items || []).map((row) => {
          const edit = edits.get(row.id);
          return edit && edit.seq > photoSeqAtStart ? { ...row, image_url: edit.url } : row;
        });
        setData({ items, total: r.data.total || 0 });
      })
      .catch(() => { if (!aborted) setData({ items: [], total: 0 }); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [catalog.id, page, sortBy, sortDir, debouncedSearch, activeFilter, refreshKey]);

  const toggleField = async (p, field) => {
    setBusyId(p.id);
    const next = !(field === "active" ? (p.active !== false) : !!p.available);
    try {
      await api.patch(`/retail/catalogs/${catalog.id}/products/${p.id}`, { [field]: next });
      // Optimistic local update so the toggle feels instant; the
      // backend is the source of truth — next refresh will reconcile.
      setData((prev) => ({
        ...prev,
        items: prev.items.map((row) => row.id === p.id ? { ...row, [field]: next } : row),
      }));
      toast.success(
        field === "active"
          ? `${p.name} is now ${next ? "active" : "inactive"}`
          : `${p.name} is now ${next ? "available" : "unavailable"}`
      );
      // Bubble up so other widgets (e.g. quality snapshot) refresh too.
      if (onReload) onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.errors?.join(", ") || "Update failed");
    } finally { setBusyId(null); }
  };

  // Product photo saved or removed inside the edit modal. Patch ONLY the
  // cached row — never `editing`, which the modal's seeding effects key
  // on, so a form with unsaved edits is never reset by a photo change.
  // The change is also recorded so a list request already in flight
  // (started before the photo was written) cannot put the old photo
  // back into this row when it lands.
  const patchRowImage = (productId, url) => {
    const edits = photoEditsRef.current;
    edits.seq += 1;
    edits.byId.set(productId, { url: url || "", seq: edits.seq });
    setData((prev) => ({
      ...prev,
      items: prev.items.map((row) => row.id === productId ? { ...row, image_url: url || "" } : row),
    }));
  };

  const fmtPrice = (p) => fmtMoney(p.price, p.currency || catalog.currency);

  // Columns wire the AdminDataGrid renderers to product fields.
  const columns = [
    {
      key: "name", header: "Name", sortable: true,
      render: (p) => (
        <div className="font-medium text-slate-900" data-testid={`retail-product-name-${p.id}`}>
          {p.name}
        </div>
      ),
    },
    {
      key: "category", header: "Category", sortable: true,
      render: (p) => <span className="text-slate-600">{p.category || "—"}</span>,
    },
    {
      key: "price", header: "Price", sortable: true, align: "right",
      render: (p) => <span className="tabular-nums">{fmtPrice(p)}</span>,
    },
    {
      key: "available", header: "Available", sortable: true, align: "center",
      render: (p) => (
        <ToggleCell
          checked={!!p.available}
          busy={busyId === p.id}
          onChange={() => toggleField(p, "available")}
          testId={`retail-product-available-toggle-${p.id}`}
          labelOn="Yes" labelOff="No"
        />
      ),
    },
    {
      key: "active", header: "Active", sortable: true, align: "center",
      render: (p) => {
        const isActive = p.active !== false;
        return (
          <button
            type="button"
            disabled={busyId === p.id}
            onClick={() => toggleField(p, "active")}
            className={
              "text-xs px-2 py-0.5 rounded-md border transition " +
              (isActive
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200")
            }
            title={isActive
              ? "Click to deactivate — hides from storefront and assistant"
              : "Click to activate — shows on storefront"}
            data-testid={`retail-product-active-toggle-${p.id}`}
          >
            {busyId === p.id ? "…" : (isActive ? "Active" : "Inactive")}
          </button>
        );
      },
    },
    {
      key: "_actions", header: "Edit", align: "right",
      render: (p) => (
        <button
          type="button"
          onClick={() => setEditing(p)}
          className="text-xs text-slate-700 underline-offset-2 hover:underline"
          data-testid={`retail-product-edit-${p.id}`}
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
      data-testid="retail-product-table"
    >
      {/* Section header — visually separates Products from the
          schema/import panels above. */}
      <header className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-amber-50">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Products</h3>
          <span className="text-xs text-slate-500" data-testid="retail-product-total-count">
            {data.total} total
          </span>
        </div>
        <div className="flex items-center gap-2">
          {onAddManual && (
            <button
              type="button"
              onClick={onAddManual}
              data-testid="retail-product-add-btn"
              className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600"
            >
              <Plus className="w-3.5 h-3.5"/> Add product
            </button>
          )}
          {/* Product attributes (Sep 2026) — closes the spreadsheet
              round trip. The exported headers are exactly the ones the
              importer accepts, so export -> edit -> re-import UPDATES
              these products (matched on SKU) instead of creating
              duplicates.

              Fetched through `api` rather than rendered as a plain
              <a href>: this app authenticates with a Bearer token from
              localStorage (see `lib/api.js`), NOT a cookie, so a plain
              link would reach the endpoint unauthenticated and 401. */}
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            data-testid="retail-product-export-btn"
            className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5"/> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button
            type="button"
            onClick={() => onReload && onReload()}
            className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50"
            data-testid="retail-product-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </header>

      <AdminDataGrid
        columns={columns}
        rows={data.items}
        getRowId={(p) => p.id}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={({ sortBy: sb, sortDir: sd }) => { setSortBy(sb); setSortDir(sd); }}
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={data.total}
        onPageChange={setPage}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search products by name, category, description…"
        filters={[
          { value: "all", label: "All" },
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        loading={loading}
        emptyState={<>No products yet. Upload a spreadsheet above to add some.</>}
        filterEmptyState={(
          <>
            No products match your filters.{" "}
            <button
              type="button"
              onClick={() => { setSearch(""); setActiveFilter("all"); }}
              className="underline-offset-2 hover:underline text-orange-700"
              data-testid="retail-product-clear-filters"
            >
              Clear filters
            </button>
          </>
        )}
        tableRowClassName={(p) => p.active !== false ? "" : "opacity-60"}
        testId="retail-product-grid"
      />

      {editing && (
        <ProductEditModal
          catalog={catalog}
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); if (onReload) onReload(); }}
          onImageChange={patchRowImage}
        />
      )}
    </section>
  );
}

// ----------------------- assistant link section --------------------------

function AssistantLinkSection({ catalog }) {
  const [assistants, setAssistants] = useState([]);
  const [links, setLinks] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, l] = await Promise.all([
        api.get("/assistants").catch(() => ({ data: { assistants: [] } })),
        api.get(`/retail/assistant-links?catalog_id=${catalog.id}`).catch(() => ({ data: { links: [] } })),
      ]);
      const list = a.data?.assistants || a.data?.items || [];
      setAssistants(list);
      setLinks(l.data?.links || []);
    } catch { /* ignore — section is non-critical */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [catalog.id]);

  const link = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post("/retail/assistant-links", { assistant_id: selected, catalog_id: catalog.id });
      setSelected("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
    finally { setBusy(false); }
  };

  const unlink = async (link_id) => {
    setBusy(true);
    try {
      await api.delete(`/retail/assistant-links/${link_id}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Unlink failed"); }
    finally { setBusy(false); }
  };

  // Build a name lookup. Assistants endpoint shape varies — defensive read.
  const nameOf = (id) => {
    const found = assistants.find((a) => a.id === id);
    return found?.assistant_name || found?.name || id;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="retail-assistant-link-section">
      <div>
        <div className="font-medium text-sm">AI Assistant for Product Questions</div>
        <div className="text-xs text-slate-500 mt-0.5" data-testid="retail-link-assistant-helper">
          Pick the assistant that answers customer questions about items in this catalog (ingredients, allergens, availability, etc.). The assistant only uses this tenant&apos;s resources.
        </div>
      </div>
      {assistants.length === 0 ? (
        <div className="text-xs text-slate-500">No assistants available yet. Create one under Resources & AI Assistant.</div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="retail-link-assistant-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
          >
            <option value="">— Select an AI Assistant —</option>
            {assistants.map((a) => <option key={a.id} value={a.id}>{a.assistant_name || a.name || a.id}</option>)}
          </select>
          <button
            data-testid="retail-link-assistant-btn"
            onClick={link}
            disabled={!selected || busy}
            className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60"
          >
            Link
          </button>
        </div>
      )}
      {links.length === 0 ? (
        <div className="text-xs text-slate-500">No assistant linked to this catalog yet.</div>
      ) : (
        <ul className="space-y-1 text-sm" data-testid="retail-link-list">
          {links.map((l) => (
            <li key={l.id} className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-1.5">
              <span>{nameOf(l.assistant_id)}</span>
              <button
                onClick={() => unlink(l.id)}
                className="text-xs text-slate-500 hover:text-red-600"
                data-testid={`retail-unlink-${l.id}`}
              >
                Unlink
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ----------------------- Phase 3L — order modes panel -----------------------

const ORDER_MODE_META = [
  { k: "dine_in",  label: "Dine-In / Walk-In",  desc: "On-premise customers order at the table, kiosk, or counter." },
  { k: "takeout",  label: "Take Out", desc: "Guests order in-store and take the food with them." },
  { k: "pickup",   label: "Pickup",   desc: "Customer orders online, picks up at a later time." },
  { k: "delivery", label: "Local Delivery", desc: "We deliver to the customer's address ourselves." },
  // FedEx Parcel Shipping Completion — carrier-neutral label by design.
  // Never say "FedEx"/"USPS"/"Carrier" here; providers stay an internal
  // implementation detail selected under Shipping Admin.
  { k: "shipping", label: "Parcel Shipping", desc: "We ship the order to the customer via a shipping carrier, with tracking." },
];

const DEFAULT_MODES_UI = {
  dine_in:  { enabled: true,  require_phone: false, require_email: false, require_phone_or_email: false, require_address: false },
  takeout:  { enabled: true,  require_phone: false, require_email: false, require_phone_or_email: false, require_address: false },
  pickup:   { enabled: false, require_phone: false, require_email: false, require_phone_or_email: true,  require_address: false },
  delivery: { enabled: false, require_phone: true,  require_email: false, require_phone_or_email: false, require_address: true },
  // Mirrors backend/retail/catalog.py::DEFAULT_ORDER_MODES["shipping"].
  shipping: { enabled: false, require_phone: false, require_email: false, require_phone_or_email: true,  require_address: true },
};

// ----------------------- Phase 3L.6 / Iter41 — storefront + chat panel ----
// Catalog-centric tenant UX: every catalog surfaces its own unique
// Storefront link (Copy + Open) plus a per-catalog Chat Assistant toggle.
// Tenants no longer need to visit the standalone Retail Links page for
// these everyday actions.
function StorefrontAndChatPanel({ catalog, onChanged }) {
  const [tenantSlug, setTenantSlug] = useState("");
  // Iter42.1 — Resolver now distinguishes three states (default storefront /
  // non-default profile / orphan). `kind` keeps the rendering branches
  // explicit so we never accidentally render the tenant root for a
  // non-default catalog again.
  //   kind: "default" | "non_default" | "orphan" | null (loading)
  const [linkInfo, setLinkInfo] = useState({ kind: null, profile: null });
  const [chatEnabled, setChatEnabled] = useState(catalog?.chat_assistant_enabled !== false);
  const [saving, setSaving] = useState(false);
  const [minting, setMinting] = useState(false);
  const [copied, setCopied] = useState(false);
  // Catalog-level Stripe authorization/tenant-review (Aug 2026).
  const [captureMode, setCaptureMode] = useState(catalog?.card_capture_mode || "automatic");
  const [savingCaptureMode, setSavingCaptureMode] = useState(false);

  // Sync local toggle state when a different catalog becomes active or the
  // parent reloads after a save round-trip.
  useEffect(() => {
    setChatEnabled(catalog?.chat_assistant_enabled !== false);
  }, [catalog?.id, catalog?.chat_assistant_enabled]);

  useEffect(() => {
    setCaptureMode(catalog?.card_capture_mode || "automatic");
  }, [catalog?.id, catalog?.card_capture_mode]);

  const reloadLinkInfo = useCallback(async () => {
    try {
      const [meR, lpR] = await Promise.all([
        api.get("/company").catch(() => null),
        api.get("/retail/link-profiles").catch(() => null),
      ]);
      const slug = meR?.data?.company?.chatbot?.slug || meR?.data?.company?.chatbot_slug || "";
      setTenantSlug(slug);
      const profiles = (lpR?.data?.link_profiles || []).filter((p) => p.status !== "archived");

      // Iter42.2 (Feb 2026) — Authoritative default detection.
      // The default URL `/retail/{tenant}` resolves to whichever single
      // catalog the public resolver picks (driven by `pick_active_catalog`).
      // The default profile's `catalog_ids` can list multiple catalogs,
      // but ONLY ONE wins resolution. Treating "any catalog in
      // default.catalog_ids" as default was the Iter42.1 regression —
      // it mis-classified the losers, surfacing the tenant-root URL even
      // though the URL actually loaded a different catalog.
      //
      // Source of truth: ask the resolver. The public catalog endpoint
      // requires no auth, so we can call it from admin without breaking
      // the impersonation/auth flow.
      let canonicalDefaultId = null;
      if (slug) {
        try {
          const defR = await api.get(`/retail/public/${slug}/catalog`);
          canonicalDefaultId = defR?.data?.catalog?.id || null;
        } catch {
          canonicalDefaultId = null;  // tenant not yet resolvable — fall through
        }
      }

      // (1) Default catalog — this catalog is THE one the resolver picks
      // for the bare tenant URL. There can be only one per tenant.
      if (canonicalDefaultId && canonicalDefaultId === catalog.id) {
        const defaultProfile = profiles.find((p) => p.is_default) || null;
        setLinkInfo({ kind: "default", profile: defaultProfile });
        return;
      }

      // (2) Non-default profile binding. Prefer a profile that lists
      // ONLY this catalog (most explicit), else the first non-default
      // profile that lists it.
      const nonDefaultExclusive = profiles.find((p) =>
        !p.is_default
        && Array.isArray(p.catalog_ids)
        && p.catalog_ids.length === 1
        && p.catalog_ids[0] === catalog.id
      );
      const nonDefault = nonDefaultExclusive || profiles.find((p) =>
        !p.is_default
        && Array.isArray(p.catalog_ids)
        && p.catalog_ids.includes(catalog.id)
      );
      if (nonDefault) {
        setLinkInfo({ kind: "non_default", profile: nonDefault });
        return;
      }

      // (3) Orphan. No non-default profile binds this catalog AND it's
      // not the resolver winner. Even if it's listed in the default
      // profile's catalog_ids, the URL would still route to the other
      // catalog, so we refuse to render a misleading URL and surface
      // the "Generate URL" CTA instead.
      setLinkInfo({ kind: "orphan", profile: null });
    } catch {
      setLinkInfo({ kind: null, profile: null });
      setTenantSlug("");
    }
  }, [catalog.id]);

  useEffect(() => {
    let alive = true;
    reloadLinkInfo().then(() => { if (!alive) return; });
    return () => { alive = false; };
  }, [reloadLinkInfo]);

  const storefrontUrl = useMemo(() => {
    if (!tenantSlug || !linkInfo.kind || linkInfo.kind === "orphan") return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (linkInfo.kind === "default") return `${origin}/retail/${tenantSlug}`;
    return `${origin}/retail/${tenantSlug}/${linkInfo.profile.link_slug}`;
  }, [tenantSlug, linkInfo]);

  const copy = async () => {
    if (!storefrontUrl) return;
    try {
      await navigator.clipboard.writeText(storefrontUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };

  const generateStorefrontUrl = async () => {
    if (minting) return;
    setMinting(true);
    try {
      // POST /retail/link-profiles handles slug normalization & uniqueness
      // server-side; we send the catalog name as the seed and let the
      // backend pick an available slug.
      await api.post("/retail/link-profiles", {
        name: catalog.name,
        link_slug_seed: catalog.name,
        description: `Storefront for ${catalog.name}`,
        catalog_ids: [catalog.id],
        order_modes: catalog.order_modes || {},
        is_default: false,
        status: "published",
      });
      toast.success("Storefront URL generated");
      await reloadLinkInfo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate storefront URL");
    } finally {
      setMinting(false);
    }
  };

  const toggleChat = async (next) => {
    if (saving) return;
    setChatEnabled(next);  // optimistic
    setSaving(true);
    try {
      await api.put(`/retail/catalogs/${catalog.id}`, { chat_assistant_enabled: next });
      toast.success(next ? "Chat Assistant enabled on this storefront" : "Chat Assistant hidden on this storefront");
      await onChanged();
    } catch (e) {
      setChatEnabled(!next);  // revert
      toast.error(e?.response?.data?.detail || "Could not update Chat Assistant");
    } finally {
      setSaving(false);
    }
  };

  const changeCaptureMode = async (next) => {
    if (savingCaptureMode || next === captureMode) return;
    const prev = captureMode;
    setCaptureMode(next);  // optimistic
    setSavingCaptureMode(true);
    try {
      await api.put(`/retail/catalogs/${catalog.id}`, { card_capture_mode: next });
      toast.success(
        next === "tenant_review"
          ? "Card payments on this catalog now require your review before capture."
          : "Card payments on this catalog will capture automatically at checkout again."
      );
      await onChanged();
    } catch (e) {
      setCaptureMode(prev);  // revert
      toast.error(e?.response?.data?.detail || "Could not update the card capture mode");
    } finally {
      setSavingCaptureMode(false);
    }
  };

  const isLive = (catalog.status || "").toLowerCase() === "published";
  const isOrphan = linkInfo.kind === "orphan";

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="retail-storefront-chat-panel">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-medium text-sm">Storefront link &amp; Chat Assistant</h3>
          <p className="text-xs text-slate-500">
            A per-catalog ordering page customers visit to browse <em>this</em> catalog and place orders. Use it for QR-table menus, kiosks, pickup, or catering. Toggle the Chat Assistant below if you want this storefront to be order-only.
          </p>
        </div>
        {!isLive && (
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200" title="Publish this catalog to make the storefront link live.">
            Not live yet
          </span>
        )}
      </header>

      {/* Storefront URL row */}
      {isOrphan ? (
        // Orphan state — no profile binds this catalog. Refuse to render a
        // misleading tenant-root URL; offer to mint a unique non-default
        // profile on demand.
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="retail-catalog-storefront-row">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wide text-amber-800">Storefront URL</div>
            <div className="text-xs text-slate-700" data-testid="retail-catalog-storefront-url">
              Not configured yet — this catalog has no storefront link.
            </div>
          </div>
          <button
            type="button"
            onClick={generateStorefrontUrl}
            disabled={minting}
            data-testid="retail-catalog-storefront-generate"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-medium disabled:opacity-60"
          >
            {minting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5"/>}
            Generate URL
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="retail-catalog-storefront-row">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700">
              Storefront URL{linkInfo.kind === "default" && " (default)"}
            </div>
            <div className="font-mono text-xs text-slate-700 break-all" data-testid="retail-catalog-storefront-url">
              {storefrontUrl || "Resolving…"}
            </div>
            {linkInfo.kind === "non_default" && linkInfo.profile && (
              <div className="text-[10px] text-slate-500 mt-0.5">Profile: <span className="font-mono">{linkInfo.profile.link_slug}</span></div>
            )}
          </div>
          <button
            type="button"
            onClick={copy}
            disabled={!storefrontUrl}
            data-testid="retail-catalog-storefront-copy"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs disabled:opacity-50"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
          <a
            href={storefrontUrl || "#"}
            target="_blank"
            rel="noreferrer"
            aria-disabled={!storefrontUrl}
            data-testid="retail-catalog-storefront-open"
            className={"inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs " + (storefrontUrl ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-200 text-slate-500 pointer-events-none")}
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open
          </a>
        </div>
      )}

      {/* Chat Assistant toggle row */}
      <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="retail-catalog-chat-toggle-row">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900">Chat Assistant on this storefront</div>
          <div className="text-xs text-slate-500">
            {chatEnabled
              ? "Customers see a “Chat with us” button on this storefront."
              : "The chat surfaces are hidden. The storefront is order-only."}
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={chatEnabled}
            onChange={(e) => toggleChat(e.target.checked)}
            disabled={saving}
            data-testid="retail-catalog-chat-assistant-toggle"
            className="h-4 w-4 cursor-pointer accent-emerald-600"
          />
          <span className="text-xs text-slate-700">{chatEnabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>

      {/* Catalog-level Stripe authorization/tenant-review (Aug 2026) */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 space-y-2" data-testid="retail-catalog-card-capture-mode-row">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900">Card payment capture</div>
          <div className="text-xs text-slate-500">
            {captureMode === "tenant_review"
              ? "Tenant review: the customer's card is authorized at checkout but NOT charged. You must review and accept the order before the customer is charged."
              : "Automatic: the customer's card is charged as soon as checkout completes (default)."}
          </div>
        </div>
        <select
          value={captureMode}
          onChange={(e) => changeCaptureMode(e.target.value)}
          disabled={savingCaptureMode}
          data-testid="retail-catalog-card-capture-mode-select"
          className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white disabled:opacity-60"
        >
          <option value="automatic">Automatic — capture during checkout</option>
          <option value="tenant_review">Tenant review — authorize at checkout, capture only after I approve</option>
        </select>
      </div>
    </section>
  );
}

export function OrderModesPanel({ catalog, onChanged }) {
  const initial = useMemo(() => {
    const out = {};
    for (const m of ORDER_MODE_META) {
      out[m.k] = { ...DEFAULT_MODES_UI[m.k], ...((catalog?.order_modes || {})[m.k] || {}) };
    }
    return out;
  }, [catalog?.order_modes]);
  const [state, setState] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setState(initial); }, [initial]);

  const toggle = (mode, key, value) => {
    setState((s) => ({ ...s, [mode]: { ...s[mode], [key]: value } }));
  };

  const save = async () => {
    if (saving) return;
    if (!ORDER_MODE_META.some((m) => state[m.k].enabled)) {
      toast.error("At least one order mode must stay enabled.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/retail/catalogs/${catalog.id}`, { order_modes: state });
      toast.success("Order modes updated");
      await onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        (detail && typeof detail === "object" ? detail.message : detail)
          || "Could not save order modes",
      );
    } finally {
      setSaving(false);
    }
  };

  // Oct 2026 — "Require OTP verification" below has its OWN Save
  // (`LoyaltyOtpVerificationSetting`): this button saves order modes only,
  // so one section's failure can never hide the other's success.
  const dirty = JSON.stringify(state) !== JSON.stringify(initial);

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="retail-order-modes-panel">
      <header className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-medium text-sm">Fulfillment Methods</h3>
          <p className="text-xs text-slate-500">Control which fulfillment methods this catalog supports + what contact info is required.</p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="retail-order-modes-save"
          className={"text-xs px-3 py-1.5 rounded-md font-medium " + ((!dirty || saving) ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-emerald-600 text-white hover:bg-emerald-700")}
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {ORDER_MODE_META.map((m) => {
          const cfg = state[m.k];
          return (
            <div
              key={m.k}
              className={"rounded-lg border p-3 " + (cfg.enabled ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50/40")}
              data-testid={`retail-order-mode-${m.k}`}
            >
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cfg.enabled}
                  onChange={(e) => toggle(m.k, "enabled", e.target.checked)}
                  data-testid={`retail-order-mode-${m.k}-enabled`}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-slate-900">{m.label}</div>
                  <div className="text-xs text-slate-500">{m.desc}</div>
                </div>
              </label>
              {cfg.enabled && (
                <div className="mt-2 pl-6 grid grid-cols-2 gap-2 text-xs text-slate-700">
                  <label className="flex items-center gap-1.5">
                    <input type="checkbox" checked={!!cfg.require_phone} onChange={(e) => toggle(m.k, "require_phone", e.target.checked)} data-testid={`retail-order-mode-${m.k}-require-phone`} />
                    Require phone
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input type="checkbox" checked={!!cfg.require_email} onChange={(e) => toggle(m.k, "require_email", e.target.checked)} data-testid={`retail-order-mode-${m.k}-require-email`} />
                    Require email
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input type="checkbox" checked={!!cfg.require_phone_or_email} onChange={(e) => toggle(m.k, "require_phone_or_email", e.target.checked)} data-testid={`retail-order-mode-${m.k}-require-phone-or-email`} />
                    Require phone OR email
                  </label>
                  {(m.k === "delivery" || m.k === "shipping") && (
                    <label className="flex items-center gap-1.5">
                      <input type="checkbox" checked={!!cfg.require_address} onChange={(e) => toggle(m.k, "require_address", e.target.checked)} data-testid={`retail-order-mode-${m.k}-require-address`} />
                      Require address
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <LoyaltyOtpVerificationSetting catalog={catalog} onChanged={onChanged} />
    </section>
  );
}



// ----------------------- Phase 3L.1 helpers -------------------------------

function CopyChip({ value, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }
        catch { toast.error("Copy failed"); }
      }}
      className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700"
      title="Copy"
    >
      <span>{value}</span>
      {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

// Phase 3L.1 — Catalog edit modal. Name, description, currency. Status +
// order modes have their own dedicated controls (publish button + the
// existing OrderModesPanel) and aren't repeated here to avoid two
// sources of truth for the same field.
function CatalogEditModal({ catalog, onClose, onSaved }) {
  const [name, setName] = useState(catalog.name || "");
  const [description, setDescription] = useState(catalog.description || "");
  const [currency, setCurrency] = useState(catalog.currency || "USD");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return toast.error("Name is required.");
    setSubmitting(true);
    try {
      await api.put(`/retail/catalogs/${catalog.id}`, {
        name: name.trim(),
        description: description.trim(),
        currency: (currency || "USD").toUpperCase(),
      });
      toast.success("Catalog updated.");
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save catalog.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6" data-testid="retail-catalog-edit-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <form onSubmit={submit} className="relative z-10 w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Edit catalog</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="retail-catalog-edit-name"
              className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="retail-catalog-edit-description"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              data-testid="retail-catalog-edit-currency"
              className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
            >
              {["USD", "INR", "EUR", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="retail-catalog-edit-cancel" className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="retail-catalog-edit-save" className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-60 inline-flex items-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin"/>}
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}

// Phase 3L.7 / Iter42 — granular duplicate flow. Operator picks the new
// name + description and toggles what to carry over from the source. The
// backend mints a fresh non-default link profile when `mint_link_profile`
// is true so the new draft catalog has a unique storefront URL the moment
// it's created.
function DuplicateCatalogModal({ catalog, onClose, onDuplicated }) {
  const [name, setName] = useState(`${catalog.name || "Catalog"} (Copy)`);
  const [description, setDescription] = useState(catalog.description || "");
  const [copyProducts, setCopyProducts] = useState(true);
  const [copyOrderModes, setCopyOrderModes] = useState(true);
  const [copyChatAssistant, setCopyChatAssistant] = useState(true);
  const [mintLinkProfile, setMintLinkProfile] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return toast.error("New catalog name is required.");
    setSubmitting(true);
    try {
      const r = await api.post(`/retail/catalogs/${catalog.id}/duplicate`, {
        name: name.trim(),
        description: description.trim(),
        copy_products: copyProducts,
        copy_order_modes: copyOrderModes,
        copy_chat_assistant: copyChatAssistant,
        mint_link_profile: mintLinkProfile,
      });
      toast.success(`Duplicated as “${r.data.catalog.name}”`);
      onDuplicated(r.data.catalog);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not duplicate catalog.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6" data-testid="retail-catalog-duplicate-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <form onSubmit={submit} className="relative z-10 w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Duplicate catalog</h2>
            <p className="text-xs text-slate-500 mt-0.5">From <span className="font-medium text-slate-700">{catalog.name}</span> — new copy lands as draft.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">New catalog name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="retail-catalog-duplicate-name"
              className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              data-testid="retail-catalog-duplicate-description"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="What's different about this variant?"
            />
          </div>

          <fieldset className="space-y-2 border border-slate-200 rounded-lg p-3">
            <legend className="text-[11px] font-medium uppercase tracking-wider text-slate-500 px-1">What to copy</legend>
            <CopyToggle
              testid="retail-catalog-duplicate-copy-products"
              checked={copyProducts}
              onChange={setCopyProducts}
              label="Copy products"
              hint={`Bring ${catalog.product_count || 0} product${(catalog.product_count || 0) === 1 ? "" : "s"} into the new catalog`}
            />
            <CopyToggle
              testid="retail-catalog-duplicate-copy-order-modes"
              checked={copyOrderModes}
              onChange={setCopyOrderModes}
              label="Copy order modes"
              hint="Carry over Walk-In / Pickup / Delivery settings"
            />
            <CopyToggle
              testid="retail-catalog-duplicate-copy-chat"
              checked={copyChatAssistant}
              onChange={setCopyChatAssistant}
              label="Copy Chat Assistant setting"
              hint="If off, the new catalog gets Chat Assistant ON by default"
            />
            <CopyToggle
              testid="retail-catalog-duplicate-mint-link"
              checked={mintLinkProfile}
              onChange={setMintLinkProfile}
              label="Auto-create a unique storefront URL"
              hint="Recommended — gives the duplicate its own shareable link"
            />
          </fieldset>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="retail-catalog-duplicate-cancel" className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="retail-catalog-duplicate-submit" className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-60 inline-flex items-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin"/>}
            Duplicate
          </button>
        </div>
      </form>
    </div>
  );
}

// Small inline component for the duplicate modal's option toggles.
function CopyToggle({ testid, checked, onChange, label, hint }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none py-1">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="mt-0.5 h-4 w-4 accent-emerald-600 cursor-pointer"
      />
      <span className="min-w-0">
        <span className="block text-sm text-slate-900">{label}</span>
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
    </label>
  );
}

// Phase 3L.1 — Manual product add. Reuses the same validation pipeline
// the import flow uses (price required, etc.) but lets the operator add
// a single row without uploading anything.
function ManualProductModal({ catalog, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sku, setSku] = useState("");
  const [tags, setTags] = useState(""); // generic tags
  // CONFIRMED DEFECT FIX (Sep 2026) — this form used to carry its own
  // free-text "Dietary" and "Allergens" inputs and post them inside an
  // `extra` object. `CreateProductIn` never declared `extra`, so
  // Pydantic v2's default `extra="ignore"` dropped the whole thing and
  // everything the operator typed was silently lost. Both inputs now
  // live in the SHARED `ProductAttributesFields` block (a typed Vegan
  // toggle + a typed allergens field), which posts typed fields the
  // create route validates through the same pipeline as every import
  // row. Sharing one block with the edit modal is also what stops the
  // two forms drifting apart again.
  const attributeConfig = catalog?.attribute_config || {};
  const [attrs, setAttrs] = useState(() => attributesFromProduct(null));
  const [available, setAvailable] = useState(true);
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Parcel Shipping Readiness & Checkout Completion — Phase 3. Same
  // shared field set + state shape as ProductEditModal.
  const [parcel, setParcel] = useState(DEFAULT_PARCEL);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return toast.error("Product name is required.");
    if (!price || isNaN(Number(price))) return toast.error("Price is required and must be a number.");
    if (parcel.eligible && !parcelIsComplete(parcel)) {
      return toast.error("Weight and all three dimensions (greater than zero) are required before this product can be marked shippable.");
    }
    setSubmitting(true);
    try {
      const splitCSV = (s) => s.split(",").map((t) => t.trim()).filter(Boolean);
      const tagList = splitCSV(tags);
      const { data } = await api.post(`/retail/catalogs/${catalog.id}/products`, {
        name: name.trim(),
        category: category.trim() || undefined,
        description: description.trim() || undefined,
        price: Number(price),
        unit: unit.trim() || undefined,
        image_url: imageUrl.trim() || undefined,
        sku: sku.trim() || undefined,
        tags: tagList,
        available,
        active,
        // Typed attribute fields — never an untyped `extra` blob.
        ...attributesToPayload(attrs, attributeConfig),
      });
      // Same "separate endpoint, only when touched" discipline as
      // ProductEditModal — a brand-new product has no persisted parcel
      // state to diff against, so any operator-entered data is worth
      // saving.
      const newProductId = data?.product?.id;
      if (newProductId && parcelHasAnyData(parcel)) {
        await api.put(`/commerce/parcel-shipping/products/${newProductId}`, parcelToApiPayload(parcel));
      }
      toast.success(`Added “${name.trim()}”`);
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail?.errors) {
        toast.error(detail.errors.join(", "));
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not add product.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8 overflow-y-auto" data-testid="retail-product-add-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <form onSubmit={submit} className="relative z-10 w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Add product manually</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 grid sm:grid-cols-2 gap-3">
          <FieldLite label="Product name *">
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="retail-product-name" className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"/>
          </FieldLite>
          <FieldLite label="Category">
            <input value={category} onChange={(e) => setCategory(e.target.value)} data-testid="retail-product-category" placeholder="Starters" className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"/>
          </FieldLite>
          <FieldLite label="Price *">
            <input value={price} onChange={(e) => setPrice(e.target.value)} type="number" step="0.01" min="0" data-testid="retail-product-price" placeholder="9.99" className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"/>
          </FieldLite>
          <FieldLite label="Unit">
            <input value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="retail-product-unit" placeholder="ea / 250g / cup" className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"/>
          </FieldLite>
          <FieldLite label="Image URL" className="sm:col-span-2">
            <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} data-testid="retail-product-image-url" placeholder="https://…/photo.jpg" className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm font-mono"/>
          </FieldLite>
          <FieldLite label="Description" className="sm:col-span-2">
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="retail-product-description" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"/>
          </FieldLite>
          <FieldLite label="SKU">
            <input value={sku} onChange={(e) => setSku(e.target.value)} data-testid="retail-product-sku" className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm font-mono"/>
          </FieldLite>
          <FieldLite label="Tags (comma-separated)">
            <input value={tags} onChange={(e) => setTags(e.target.value)} data-testid="retail-product-tags" placeholder="popular, seasonal" className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"/>
          </FieldLite>
          <FieldLite label="Available">
            <label className="text-sm inline-flex items-center gap-2 h-10">
              <input type="checkbox" checked={available} onChange={(e) => setAvailable(e.target.checked)} data-testid="retail-product-available"/>
              Show on storefront
            </label>
          </FieldLite>
          <FieldLite label="Active">
            <label className="text-sm inline-flex items-center gap-2 h-10">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="retail-product-active"/>
              Sellable
            </label>
          </FieldLite>
        </div>
        <div className="px-5 pb-5 space-y-4">
          <ProductAttributesFields
            value={attrs}
            onChange={setAttrs}
            config={attributeConfig}
            vertical={catalog?.vertical}
            testIdPrefix="product-add"
          />
          <ParcelShippingFields value={parcel} onChange={setParcel} />
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="retail-product-add-cancel" className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="retail-product-add-save" className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-60 inline-flex items-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin"/>}
            Add product
          </button>
        </div>
      </form>
    </div>
  );
}

function FieldLite({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
